import axios, { AxiosInstance } from 'axios'
import { toast } from '@/hooks/use-toast'
import { toDateInputValue } from '@/lib/utils'
import type {
  Account,
  Contact,
  Opportunity,
  OpportunityDetail,
  Lead,
  LeadDetail,
  Task,
  Event,
  EventAttendee,
  EventStats,
  Email,
  EmailStats,
  Quote,
  QuoteLineItem,
  OpportunityLineItem,
  DiscountDecisionResult,
  OpportunityContactRole,
  Product,
  Pricebook,
  PricebookEntry,
  Order,
  EmailTemplateGroup,
  OrderEmailPreview,
  OrderItem,
  OrderStats,
  User,
  UserType,
  DashboardStats,
  ClosedWonMapData,
  ClosedWonMapParams,
  HrEmployeeMapData,
  HrEmployeeMapParams,
  NoTaskItems,
  RenewalsDueData,
  StaleOpportunitiesData,
  RecentActivity,
  ApiResponse,
  PaginationParams,
  Activity,
  ActivityTimelineResponse,
  ActivityTimelineParams,
  HistoryTimelineResponse,
  HistoryTimelineParams,
  HistoryStats,
  OpportunityHistoryResponse,
  OpportunityHistoryParams,
  ActivityType,
  ContentVersion,
  ContentStats,
  ContentQueryParams,
  UpdateContentData,
  LinkContentData,
  Note,
  LeadSourceAnalysisData,
  SalesForecastData,
  RevenueMetric,
  PipelineStageMetric,
  ReportQueryParams,
  CustomReport,
  SavedReportMetadata,
  ReportExecutionResult,
  CreateReportData,
  UpdateReportData,
  ExecuteReportData,
  Ticket,
  TicketComment,
  TicketStats,
  DuplicateCheckResult,
  DuplicateMatch,
  DuplicateStats,
  MergePreview,
  FieldSelection,
  ContentPreviewResponse,
  DealHealth,
  LeadScore,
  StageVelocity,
  WinProbability,
  AccountHealth,
  AccountEngagement,
  CallLog,
  CallLogStats,
  PhoneLookupResult,
  PicklistValue,
  PicklistData,
  RenewalTaskTemplate,
  AccountResearchResult,
  BillingsFiscalYear,
  BillingsOverview,
  BillingsMatrix,
  BillingsRepDetail,
  BillingsImport,
  BillingsPreview,
  BillingsCommitResult,
  BillingsAttribution,
  BillingsLegacyPreview,
  BillingsLegacyCommitResult,
  BillingsTarget,
  BillingsForecast,
  BillingsTargetInput,
  BillingsRep,
  BillingsRateIssue,
  BillingsWipeImpact,
  BillingsTransactionsPage,
  ContractReview,
  ContractReviewsPage,
  WelcomeSummary,
  ContractReviewComment,
  ContractReviewDocument,
  ContractReviewStatus,
} from '@/types'

/**
 * What every Sales Trackers read is scoped by: a fiscal year, and optionally a window
 * inside it. `period` absent means the whole year, so old links keep working.
 */
export interface BillingsQuery {
  fy?: number
  basis?: string
  period?: 'year' | 'quarter' | 'month'
  /** 'YYYY-MM', when period is month. */
  month?: string
  /** 1-4, when period is quarter. */
  quarter?: number
}

/** A billings query plus the transaction list's own paging and filters. */
export interface BillingsTransactionsQuery extends BillingsQuery {
  page?: number
  limit?: number
  search?: string
  /** A user id for a person, or a label for the buckets that are not people. */
  rep?: string
  category?: string
  /** The By Product row label, which is not the same thing as a category. */
  product_group?: string
  /** Absent means every line; the two values are opposites. */
  house?: 'only' | 'exclude'
}
import type {
  GoogleIntegration,
  IntegrationStatus,
  AuthUrlResponse,
  SyncResponse,
  IntegrationSettings,
  SyncLog,
} from '@/types/integration'

// Exported axios instance for direct API calls
export const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth interceptor to exported api instance
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // Add impersonation header if active
  const impersonatedUserId = localStorage.getItem('gracklecrm_impersonated_user_id')
  if (impersonatedUserId) {
    config.headers['X-Impersonate-User-Id'] = impersonatedUserId
  }
  // HR "preview as" — read-only, HR-admin gated server-side.
  const hrPreviewId = localStorage.getItem('grm_hr_preview_employee_id')
  if (hrPreviewId) {
    config.headers['X-Impersonate-Employee-Id'] = hrPreviewId
  }
  return config
})

// --- Silent token refresh (shared by both axios instances) ---
// On a 401, exchange the long-lived refresh token for a fresh access token and
// retry the original request once, instead of forcing a full re-login (OAuth).
// A single-flight promise ensures concurrent 401s trigger only one refresh.
//
// Outcomes are three-way ON PURPOSE: only a definitive server rejection of the
// refresh token ('dead': HTTP 400/401 from /auth/refresh) may end the session.
// A 429/5xx/network failure ('transient') must NOT log the user out — treating
// every failure as fatal turned a single throttled or cold-start response on
// the once-a-day refresh into an intermittent "session expired" logout.
type RefreshOutcome = 'ok' | 'dead' | 'transient'
let refreshPromise: Promise<RefreshOutcome> | null = null

async function performTokenRefresh(): Promise<RefreshOutcome> {
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) return 'dead'
  try {
    // Bare axios (no interceptors) so a failed refresh can't recurse.
    const { data } = await axios.post('/api/v1/auth/refresh', { refreshToken })
    if (data?.accessToken) {
      localStorage.setItem('accessToken', data.accessToken)
      return 'ok'
    }
    return 'transient'
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status
    return status === 400 || status === 401 ? 'dead' : 'transient'
  }
}

function refreshSession(): Promise<RefreshOutcome> {
  if (!refreshPromise) {
    refreshPromise = performTokenRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

function clearSessionAndRedirect() {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('user')
  if (window.location.pathname !== '/login') {
    window.location.href = '/login?error=session_expired'
  }
}

// Refresh-and-retry on 401 for the exported `api` instance (no other handling).
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = (error.config || {}) as Record<string, any>
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      localStorage.getItem('refreshToken')
    ) {
      originalRequest._retry = true
      const outcome = await refreshSession()
      if (outcome === 'ok') {
        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('accessToken')}`
        return api(originalRequest)
      }
      if (outcome === 'dead') clearSessionAndRedirect()
      // 'transient': keep the session; the request fails visibly and the next
      // one retries the refresh.
    }
    return Promise.reject(error)
  }
)

export interface MailchimpSyncStatusCounts {
  total: number
  subscribed: number
  transactional: number
  unsubscribed: number
}

export interface MailchimpSyncDiff {
  audienceId: string
  currentMembers: number
  distinctCandidates: number
  newMembers: number
  updates: number
  downgradeCount: number
  downgradeSample: { email: string; from: string; to: string }[]
}

export interface MailchimpSyncPreview {
  contacts: MailchimpSyncStatusCounts
  leads: MailchimpSyncStatusCounts
  totals: MailchimpSyncStatusCounts
  distinctEmails: number
  // Present when a preview is requested with an audienceId (Tier 2 diff).
  diff?: MailchimpSyncDiff
  diffWarning?: string
}

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: '/api/v1',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Add request interceptor for auth token and impersonation
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('accessToken')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      // Add impersonation header if active
      const impersonatedUserId = localStorage.getItem('gracklecrm_impersonated_user_id')
      if (impersonatedUserId) {
        config.headers['X-Impersonate-User-Id'] = impersonatedUserId
      }
      // HR "preview as" — read-only, HR-admin gated server-side.
      const hrPreviewId = localStorage.getItem('grm_hr_preview_employee_id')
      if (hrPreviewId) {
        config.headers['X-Impersonate-Employee-Id'] = hrPreviewId
      }
      return config
    })

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 503 && error.response?.data?.error === 'maintenance') {
          // Store maintenance state for the app to pick up
          const message = error.response.data.message || 'System is under maintenance.'
          window.dispatchEvent(
            new CustomEvent('maintenance-mode', { detail: { enabled: true, message } })
          )
        } else if (error.response?.status === 401) {
          // Try a silent token refresh + retry before giving up. Log out ONLY
          // when the server definitively rejects the refresh token ('dead').
          // Previously clearSessionAndRedirect() ran unguarded at the end of
          // this branch, so a retried request that 401'd twice — or any 401
          // that wasn't a session problem at all ("account inactive",
          // "Authentication required", …) — wiped the session on the spot:
          // the source of intermittent "session expired" logouts.
          const originalRequest = (error.config || {}) as Record<string, any>
          if (!originalRequest._retry && localStorage.getItem('refreshToken')) {
            originalRequest._retry = true
            const outcome = await refreshSession()
            if (outcome === 'ok') {
              originalRequest.headers = originalRequest.headers || {}
              originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('accessToken')}`
              return this.client(originalRequest)
            }
            if (outcome === 'dead') clearSessionAndRedirect()
            // 'transient': keep the session — the request fails visibly and
            // the next request retries the refresh.
          }
          // _retry exhausted or no refresh token: reject without logging out;
          // the caller's error handling explains the failure.
        } else if (error.response?.status === 429) {
          // Surface the rate limiter's descriptive message so it isn't masked by
          // a generic per-component "failed to load" error — and tell the user
          // concretely how long to wait (the server sends retryAfter seconds).
          const data = error.response.data || {}
          const secs = data.retryAfter
          const mins =
            typeof secs === 'number' && secs > 0 ? Math.max(1, Math.ceil(secs / 60)) : null
          const wait = mins
            ? ` You can try again in about ${mins} minute${mins === 1 ? '' : 's'}.`
            : ' Please wait a moment and try again.'
          toast({
            title: data.error || 'Too many requests',
            description: (data.message || 'You are sending requests too quickly.') + wait,
            variant: 'destructive',
          })
        }
        return Promise.reject(error)
      }
    )
  }

  // Dashboard
  async getDashboardStats(params?: {
    owner_id?: string
    startDate?: string
    endDate?: string
  }): Promise<DashboardStats> {
    const { data } = await this.client.get<ApiResponse<DashboardStats>>('/dashboard/stats', {
      params,
    })
    return data.data
  }

  async getClosedWonMap(params?: ClosedWonMapParams): Promise<ClosedWonMapData> {
    const { data } = await this.client.get<ApiResponse<ClosedWonMapData>>('/closed-won-map', {
      params,
    })
    return data.data
  }

  async getHrEmployeeMap(params?: HrEmployeeMapParams): Promise<HrEmployeeMapData> {
    const { data } = await this.client.get<ApiResponse<HrEmployeeMapData>>('/hr-employee-map', {
      params,
    })
    return data.data
  }

  async getRecentActivity(limit = 10, params?: { owner_id?: string }): Promise<RecentActivity[]> {
    const { data } = await this.client.get<ApiResponse<RecentActivity[]>>('/dashboard/activity', {
      params: { limit, ...params },
    })
    return data.data
  }

  async getNoTaskItems(params?: { owner_id?: string }): Promise<NoTaskItems> {
    const { data } = await this.client.get<ApiResponse<NoTaskItems>>('/dashboard/no-task-items', {
      params,
    })
    return data.data
  }

  async getRenewalsDue(params?: { owner_id?: string; days?: number }): Promise<RenewalsDueData> {
    const { data } = await this.client.get<ApiResponse<RenewalsDueData>>(
      '/dashboard/renewals-due',
      { params }
    )
    return data.data
  }

  async getStaleOpportunities(params?: {
    owner_id?: string
    days?: number
  }): Promise<StaleOpportunitiesData> {
    const { data } = await this.client.get<ApiResponse<StaleOpportunitiesData>>(
      '/dashboard/stale-opportunities',
      { params }
    )
    return data.data
  }

  // Accounts
  async getAccounts(params?: PaginationParams): Promise<ApiResponse<Account[]>> {
    const { data } = await this.client.get<ApiResponse<Account[]>>('/accounts', { params })
    return data
  }

  async getAccount(id: string): Promise<Account> {
    const { data } = await this.client.get<ApiResponse<Account>>(`/accounts/${id}`)
    return data.data
  }

  async getAccountHealth(id: string): Promise<AccountHealth> {
    const { data } = await this.client.get<ApiResponse<AccountHealth>>(`/accounts/${id}/health`)
    return data.data
  }

  async getAccountEngagement(id: string): Promise<AccountEngagement> {
    const { data } = await this.client.get<ApiResponse<AccountEngagement>>(
      `/accounts/${id}/engagement`
    )
    return data.data
  }

  // ---------------------------------------------------------------------
  // Sales Trackers (QuickBooks billings) - Admin only
  // ---------------------------------------------------------------------

  async getBillingsFiscalYears(): Promise<BillingsFiscalYear[]> {
    const { data } =
      await this.client.get<ApiResponse<BillingsFiscalYear[]>>('/billings/fiscal-years')
    return data.data
  }

  /** All four dashboard blocks in one call, so they cannot disagree. */
  async getBillingsOverview(params: BillingsQuery): Promise<BillingsOverview> {
    const { data } = await this.client.get<ApiResponse<BillingsOverview>>('/billings/overview', {
      params,
    })
    return data.data
  }

  /** Retire or reinstate a sales account. Changes no reported figure. */
  async setBillingsCategoryActive(body: {
    sales_account: string
    is_active: boolean
  }): Promise<{ sales_account: string; is_active: boolean }> {
    const { data } = await this.client.put<
      ApiResponse<{ sales_account: string; is_active: boolean }>
    >('/billings/attribution/categories/active', body)
    return data.data
  }

  /** Roster rows UNION labels present in the year's data. */
  async getBillingsRepRoster(params: {
    fy?: number
    include_retired?: boolean
  }): Promise<BillingsRep[]> {
    const { data } = await this.client.get<ApiResponse<BillingsRep[]>>('/billings/rep-roster', {
      params,
    })
    return data.data
  }

  async addBillingsRep(body: {
    rep_label: string
    rep_user_id?: string | null
    note?: string
  }): Promise<BillingsRep> {
    const { data } = await this.client.post<ApiResponse<BillingsRep>>('/billings/rep-roster', body)
    return data.data
  }

  /** Retire or reinstate. One endpoint both ways, like picklist values. */
  async setBillingsRepActive(body: {
    roster_id?: number | null
    rep_label?: string
    is_active: boolean
  }): Promise<BillingsRep> {
    const { data } = await this.client.put<ApiResponse<BillingsRep>>('/billings/rep-roster', body)
    return data.data
  }

  /**
   * The Monday sales-meeting table. `month` defaults server-side to the current month
   * in Toronto, not the browser's month, so it matches every other billings figure.
   * `week` reopens a past capture.
   */
  async getBillingsForecast(params: { month?: string; week?: string }): Promise<BillingsForecast> {
    const { data } = await this.client.get<ApiResponse<BillingsForecast>>('/billings/forecast', {
      params,
    })
    return data.data
  }

  async getBillingsTargets(params: {
    fy?: number
    scope?: 'company' | 'rep'
  }): Promise<{ fiscal_year: number; targets: BillingsTarget[] }> {
    const { data } = await this.client.get<
      ApiResponse<{ fiscal_year: number; targets: BillingsTarget[] }>
    >('/billings/targets', { params })
    return data.data
  }

  /** One transaction server-side: a half-saved year would read as a shortfall. */
  async saveBillingsTargets(
    targets: BillingsTargetInput[]
  ): Promise<{ saved: number; cleared: number }> {
    const { data } = await this.client.put<ApiResponse<{ saved: number; cleared: number }>>(
      '/billings/targets',
      { targets }
    )
    return data.data
  }

  async getBillingsProducts(params: BillingsQuery): Promise<BillingsMatrix> {
    const { data } = await this.client.get<ApiResponse<BillingsMatrix>>('/billings/products', {
      params,
    })
    return data.data
  }

  async getBillingsReps(params: BillingsQuery): Promise<BillingsMatrix> {
    const { data } = await this.client.get<ApiResponse<BillingsMatrix>>('/billings/reps', {
      params,
    })
    return data.data
  }

  /** `ident` is a user id for a person, or a label for buckets like GrackleDocs. */
  async getBillingsRepDetail(ident: string, params: BillingsQuery): Promise<BillingsRepDetail> {
    const { data } = await this.client.get<ApiResponse<BillingsRepDetail>>(
      `/billings/reps/${encodeURIComponent(ident)}`,
      { params }
    )
    return data.data
  }

  /**
   * The signed-in rep's own sheet. Takes no rep identifier: the server uses the session,
   * so there is nothing here to point at somebody else.
   */
  /**
   * The invoice lines behind a period.
   *
   * The ONE billings read that returns a paged envelope rather than `{ data }`, so it does
   * not unwrap `data.data` like its neighbours.
   */
  async getBillingsTransactions(
    params: BillingsTransactionsQuery
  ): Promise<BillingsTransactionsPage> {
    const { data } = await this.client.get<BillingsTransactionsPage>('/billings/transactions', {
      params,
    })
    return data
  }

  /** The signed-in rep's own lines. Takes no identifier: the server uses the session. */
  async getBillingsMyTransactions(
    params: BillingsTransactionsQuery
  ): Promise<BillingsTransactionsPage> {
    const { data } = await this.client.get<BillingsTransactionsPage>('/billings/me/transactions', {
      params,
    })
    return data
  }

  async getBillingsMySheet(params: BillingsQuery): Promise<BillingsRepDetail> {
    const { data } = await this.client.get<ApiResponse<BillingsRepDetail>>('/billings/me/sheet', {
      params,
    })
    return data.data
  }

  async getBillingsMyFiscalYears(): Promise<BillingsFiscalYear[]> {
    const { data } = await this.client.get<ApiResponse<BillingsFiscalYear[]>>(
      '/billings/me/fiscal-years'
    )
    return data.data
  }

  async getBillingsWipeImpact(fy: number): Promise<BillingsWipeImpact> {
    const { data } = await this.client.get<ApiResponse<BillingsWipeImpact>>(
      `/billings/fiscal-years/${fy}/impact`
    )
    return data.data
  }

  /**
   * Permanently removes a fiscal year's uploaded lines. `confirm` must be the year
   * itself, e.g. "FY2027"; the server checks it too, so the dialog cannot be the only
   * thing standing between a stray click and the data.
   */
  async wipeBillingsFiscalYear(
    fy: number,
    confirm: string
  ): Promise<{ fiscal_year: number; lines_removed: number; amount_removed: number }> {
    const { data } = await this.client.delete<
      ApiResponse<{ fiscal_year: number; lines_removed: number; amount_removed: number }>
    >(`/billings/fiscal-years/${fy}`, { data: { confirm } })
    return data.data
  }

  /** Returns the history plus any rows sitting at a rate their entity would not choose. */
  async getBillingsImports(): Promise<{
    imports: BillingsImport[]
    rateIssues: BillingsRateIssue[]
  }> {
    const { data } = await this.client.get<
      ApiResponse<BillingsImport[]> & { rate_issues?: BillingsRateIssue[] }
    >('/billings/imports')
    return { imports: data.data, rateIssues: data.rate_issues ?? [] }
  }

  /**
   * Parse and report. Writes nothing.
   *
   * Explicit timeout: both axios instances in this file omit `timeout`, so the
   * default is infinite, and parsing 35,000 rows is not instant.
   */
  async previewBillingsImport(
    file: File,
    options?: { fxToUsd?: string; fxNote?: string }
  ): Promise<BillingsPreview> {
    const form = new FormData()
    form.append('file', file)
    if (options?.fxToUsd) form.append('fx_to_usd', options.fxToUsd)
    if (options?.fxNote) form.append('fx_note', options.fxNote)
    const { data } = await this.client.post<ApiResponse<BillingsPreview>>(
      '/billings/imports/preview',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 }
    )
    return data.data
  }

  /**
   * Preview the Sales Tracker workbook. Reads both Data Input tabs and the
   * Corrected Sales Rep column in one pass.
   */
  async previewBillingsLegacyImport(
    file: File,
    options?: { fxNa?: string; fxAu?: string }
  ): Promise<BillingsLegacyPreview> {
    const form = new FormData()
    form.append('file', file)
    if (options?.fxNa) form.append('fx_na', options.fxNa)
    if (options?.fxAu) form.append('fx_au', options.fxAu)
    const { data } = await this.client.post<ApiResponse<BillingsLegacyPreview>>(
      '/billings/imports/legacy/preview',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 }
    )
    return data.data
  }

  async commitBillingsLegacyImport(previewToken: string): Promise<BillingsLegacyCommitResult> {
    const { data } = await this.client.post<ApiResponse<BillingsLegacyCommitResult>>(
      '/billings/imports/legacy/commit',
      { preview_token: previewToken },
      { timeout: 300000 }
    )
    return data.data
  }

  async commitBillingsImport(previewToken: string): Promise<BillingsCommitResult> {
    const { data } = await this.client.post<ApiResponse<BillingsCommitResult>>(
      '/billings/imports/commit',
      { preview_token: previewToken },
      { timeout: 180000 }
    )
    return data.data
  }

  async getBillingsAttribution(params?: { fy?: number }): Promise<BillingsAttribution> {
    const { data } = await this.client.get<ApiResponse<BillingsAttribution>>(
      '/billings/attribution',
      { params }
    )
    return data.data
  }

  async saveBillingsAlias(payload: {
    raw_value: string
    rep_user_id?: string | null
    rep_label?: string
    is_house?: boolean
  }): Promise<unknown> {
    const { data } = await this.client.put<ApiResponse<unknown>>(
      '/billings/attribution/aliases',
      payload
    )
    return data.data
  }

  async saveBillingsCategory(payload: {
    sales_account: string
    category: string
    product_group_label?: string
    crm_family?: string
  }): Promise<unknown> {
    const { data } = await this.client.put<ApiResponse<unknown>>(
      '/billings/attribution/categories',
      payload
    )
    return data.data
  }

  /** Keyed on the content hash, so it covers every identical line. */
  async saveBillingsOverride(payload: {
    line_hash: string
    rep_user_id?: string | null
    rep_label?: string
    is_house?: boolean
    reason?: string
  }): Promise<{ lines_affected: number }> {
    const { data } = await this.client.put<ApiResponse<{ lines_affected: number }>>(
      '/billings/attribution/overrides',
      payload
    )
    return data.data
  }

  async createAccount(account: Partial<Account>): Promise<Account> {
    const { data } = await this.client.post<ApiResponse<Account>>('/accounts', account)
    return data.data
  }

  /**
   * Ask the backend to read a prospect's website and propose account fields.
   *
   * An explicit timeout and signal are required here: both axios instances in
   * this file omit `timeout`, so the default is 0 (infinite). Reading several
   * pages through Vertex can take tens of seconds, and without these the tab
   * would hang with no way out.
   */
  async researchAccountWebsite(
    website: string,
    options?: { signal?: AbortSignal; timeoutMs?: number }
  ): Promise<AccountResearchResult> {
    const { data } = await this.client.post<ApiResponse<AccountResearchResult>>(
      '/accounts/research',
      { website },
      { signal: options?.signal, timeout: options?.timeoutMs ?? 90000 }
    )
    return data.data
  }

  async updateAccount(
    id: string,
    account: Partial<Account> & { notify_owner?: boolean }
  ): Promise<Account> {
    const { data } = await this.client.put<ApiResponse<Account>>(`/accounts/${id}`, account)
    return data.data
  }

  async deleteAccount(id: string): Promise<void> {
    await this.client.delete(`/accounts/${id}`)
  }

  async bulkDeleteAccounts(ids: string[]): Promise<{ success: boolean; count: number }> {
    const { data } = await this.client.post('/accounts/bulk-delete', { ids })
    return data
  }

  async bulkUpdateAccounts(
    ids: string[],
    updates: Record<string, any>
  ): Promise<{ success: boolean; count: number; updates: Record<string, any> }> {
    const { data } = await this.client.post('/accounts/bulk-update', { ids, updates })
    return data
  }

  async bulkAssignAccounts(
    ids: string[],
    owner_id: string,
    notify_owner = true
  ): Promise<{ success: boolean; count: number; owner_id: string }> {
    const { data } = await this.client.post('/accounts/bulk-assign', {
      ids,
      owner_id,
      notify_owner,
    })
    return data
  }

  // Contacts
  async getContacts(params?: PaginationParams): Promise<ApiResponse<Contact[]>> {
    const { data } = await this.client.get<ApiResponse<Contact[]>>('/contacts', { params })
    return data
  }

  async getContact(id: string): Promise<Contact> {
    const { data } = await this.client.get<ApiResponse<Contact>>(`/contacts/${id}`)
    return data.data
  }

  async createContact(contact: Partial<Contact>): Promise<Contact & { warnings?: string[] }> {
    const { data } = await this.client.post<ApiResponse<Contact> & { warnings?: string[] }>(
      '/contacts',
      contact
    )
    // Surface any non-fatal side-effect warnings (e.g. Marketing Status sync).
    return { ...data.data, warnings: data.warnings }
  }

  async updateContact(
    id: string,
    contact: Partial<Contact> & { notify_owner?: boolean }
  ): Promise<Contact & { warnings?: string[] }> {
    const { data } = await this.client.put<ApiResponse<Contact> & { warnings?: string[] }>(
      `/contacts/${id}`,
      contact
    )
    // Surface any non-fatal side-effect warnings (e.g. Marketing Status sync).
    return { ...data.data, warnings: data.warnings }
  }

  async deleteContact(id: string): Promise<void> {
    await this.client.delete(`/contacts/${id}`)
  }

  async updateContactMailchimpTags(id: string, tags: string[]): Promise<string[]> {
    const { data } = await this.client.put<ApiResponse<{ tags: string[] }>>(
      `/contacts/${id}/mailchimp-tags`,
      { tags }
    )
    return data.data.tags
  }

  async updateLeadMailchimpTags(id: string, tags: string[]): Promise<string[]> {
    const { data } = await this.client.put<ApiResponse<{ tags: string[] }>>(
      `/leads/${id}/mailchimp-tags`,
      { tags }
    )
    return data.data.tags
  }

  async bulkDeleteContacts(ids: string[]): Promise<{ success: boolean; count: number }> {
    const { data } = await this.client.post('/contacts/bulk-delete', { ids })
    return data
  }

  async bulkUpdateContacts(
    ids: string[],
    updates: Record<string, any>
  ): Promise<{ success: boolean; count: number; updates: Record<string, any> }> {
    const { data } = await this.client.post('/contacts/bulk-update', { ids, updates })
    return data
  }

  async bulkAssignContacts(
    ids: string[],
    owner_id: string,
    notify_owner = true
  ): Promise<{ success: boolean; count: number; owner_id: string }> {
    const { data } = await this.client.post('/contacts/bulk-assign', {
      ids,
      owner_id,
      notify_owner,
    })
    return data
  }

  // Opportunities
  async getOpportunities(params?: PaginationParams): Promise<ApiResponse<Opportunity[]>> {
    const { data } = await this.client.get<ApiResponse<Opportunity[]>>('/opportunities', { params })
    return data
  }

  async getOpportunity(id: string): Promise<Opportunity> {
    const { data } = await this.client.get<ApiResponse<Opportunity>>(`/opportunities/${id}`)
    return data.data
  }

  async getOpportunityDetail(id: string): Promise<OpportunityDetail> {
    const { data } = await this.client.get<ApiResponse<OpportunityDetail>>(`/opportunities/${id}`)
    return data.data
  }

  async getDealHealth(id: string): Promise<DealHealth> {
    const { data } = await this.client.get<ApiResponse<DealHealth>>(`/opportunities/${id}/health`)
    return data.data
  }

  async getStageVelocity(id: string): Promise<StageVelocity> {
    const { data } = await this.client.get<ApiResponse<StageVelocity>>(
      `/opportunities/${id}/velocity`
    )
    return data.data
  }

  async getWinProbability(id: string): Promise<WinProbability> {
    const { data } = await this.client.get<ApiResponse<WinProbability>>(
      `/opportunities/${id}/win-probability`
    )
    return data.data
  }

  async createOpportunity(opportunity: Partial<Opportunity>): Promise<Opportunity> {
    const { data } = await this.client.post<ApiResponse<Opportunity>>('/opportunities', opportunity)
    return data.data
  }

  async updateOpportunity(
    id: string,
    opportunity: Partial<Opportunity> & {
      notify_owner?: boolean
      // Marketing Status decision on Closed Lost (applied to primary contact)
      marketing_decision?: 'Nurture' | 'Contact' | 'Dead'
      marketing_recheck_date?: string
      bdr_assignee_id?: string
    }
  ): Promise<Opportunity> {
    const { data } = await this.client.put<ApiResponse<Opportunity>>(
      `/opportunities/${id}`,
      opportunity
    )
    return data.data
  }

  async deleteOpportunity(id: string): Promise<void> {
    await this.client.delete(`/opportunities/${id}`)
  }

  async bulkDeleteOpportunities(ids: string[]): Promise<{ success: boolean; count: number }> {
    const { data } = await this.client.post('/opportunities/bulk-delete', { ids })
    return data
  }

  async bulkUpdateOpportunities(
    ids: string[],
    updates: Record<string, any>
  ): Promise<{ success: boolean; count: number; updates: Record<string, any> }> {
    const { data } = await this.client.post('/opportunities/bulk-update', { ids, updates })
    return data
  }

  async bulkAssignOpportunities(
    ids: string[],
    owner_id: string,
    notify_owner = true
  ): Promise<{ success: boolean; count: number; owner_id: string }> {
    const { data } = await this.client.post('/opportunities/bulk-assign', {
      ids,
      owner_id,
      notify_owner,
    })
    return data
  }

  // Leads
  async getLeads(params?: PaginationParams): Promise<ApiResponse<Lead[]>> {
    const { data } = await this.client.get<ApiResponse<Lead[]>>('/leads', { params })
    return data
  }

  async getLead(id: string): Promise<Lead> {
    const { data } = await this.client.get<ApiResponse<Lead>>(`/leads/${id}`)
    return data.data
  }

  async getLeadScore(id: string): Promise<LeadScore> {
    const { data } = await this.client.get<ApiResponse<LeadScore>>(`/leads/${id}/score`)
    return data.data
  }

  async getLeadDetail(id: string): Promise<LeadDetail> {
    const { data } = await this.client.get<ApiResponse<LeadDetail>>(`/leads/${id}`)
    return data.data
  }

  async createLead(lead: Partial<Lead>): Promise<Lead> {
    const { data } = await this.client.post<ApiResponse<Lead>>('/leads', lead)
    return data.data
  }

  async updateLead(
    id: string,
    lead: Partial<Lead> & { notify_owner?: boolean }
  ): Promise<Lead & { warnings?: string[] }> {
    const { data } = await this.client.put<ApiResponse<Lead> & { warnings?: string[] }>(
      `/leads/${id}`,
      lead
    )
    // Surface any non-fatal side-effect warnings (e.g. Marketing Status sync).
    return { ...data.data, warnings: data.warnings }
  }

  async deleteLead(id: string): Promise<void> {
    await this.client.delete(`/leads/${id}`)
  }

  async bulkDeleteLeads(ids: string[]): Promise<{ success: boolean; count: number }> {
    const { data } = await this.client.post('/leads/bulk-delete', { ids })
    return data
  }

  async bulkUpdateLeads(
    ids: string[],
    updates: Record<string, any>
  ): Promise<{ success: boolean; count: number; updates: Record<string, any> }> {
    const { data } = await this.client.post('/leads/bulk-update', { ids, updates })
    return data
  }

  async bulkAssignLeads(
    ids: string[],
    owner_id: string,
    notify_owner = true
  ): Promise<{ success: boolean; count: number; owner_id: string }> {
    const { data } = await this.client.post('/leads/bulk-assign', { ids, owner_id, notify_owner })
    return data
  }

  async convertLead(
    id: string,
    options: {
      createAccount?: boolean
      accountName?: string
      accountId?: string
      createContact?: boolean
      contactId?: string
      createOpportunity?: boolean
      opportunityName?: string
      opportunityAmount?: number
      opportunityCloseDate?: string
      opportunityStage?: string
      opportunityId?: string
      ownerId?: string
      convertedStatus?: string
      accountPhone?: string
      accountBillingStreet?: string
      accountBillingCity?: string
      accountBillingState?: string
      accountBillingPostalCode?: string
      accountBillingCountry?: string
    }
  ): Promise<{
    lead: Lead
    contact: Contact
    account: Account | null
    opportunity: Opportunity | null
  }> {
    const { data } = await this.client.post<{
      data: {
        lead: Lead
        contact: Contact
        account: Account | null
        opportunity: Opportunity | null
      }
    }>(`/leads/${id}/convert`, options)
    return data.data
  }

  // Tasks
  async getTasks(params?: PaginationParams): Promise<ApiResponse<Task[]>> {
    // For date filters ("today"/"overdue"/etc.) send the browser's LOCAL date so the
    // backend computes "today" in the user's timezone, not the server's UTC date.
    const query: Record<string, unknown> = { ...params }
    if (query.date_filter) {
      query.client_date = toDateInputValue(new Date())
    }
    const { data } = await this.client.get<ApiResponse<Task[]>>('/tasks', { params: query })
    return data
  }

  async getTaskStats(): Promise<{
    in_progress: number
    completed: number
    not_started: number
    total: number
  }> {
    const { data } = await this.client.get<{
      data: { in_progress: string; completed: string; not_started: string; total: string }
    }>('/tasks/stats')
    return {
      in_progress: parseInt(data.data.in_progress),
      completed: parseInt(data.data.completed),
      not_started: parseInt(data.data.not_started),
      total: parseInt(data.data.total),
    }
  }

  async getTask(id: string): Promise<Task> {
    const { data } = await this.client.get<ApiResponse<Task>>(`/tasks/${id}`)
    return data.data
  }

  async createTask(task: Partial<Task>): Promise<Task> {
    const { data } = await this.client.post<ApiResponse<Task>>('/tasks', task)
    return data.data
  }

  async updateTask(id: string, task: Partial<Task> & { notify_owner?: boolean }): Promise<Task> {
    const { data } = await this.client.put<ApiResponse<Task>>(`/tasks/${id}`, task)
    return data.data
  }

  async deleteTask(id: string): Promise<void> {
    await this.client.delete(`/tasks/${id}`)
  }

  async bulkDeleteTasks(ids: string[]): Promise<{ success: boolean; count: number }> {
    const { data } = await this.client.post('/tasks/bulk-delete', { ids })
    return data
  }

  async bulkUpdateTasks(
    ids: string[],
    updates: Record<string, any>
  ): Promise<{ success: boolean; count: number; updates: Record<string, any> }> {
    const { data } = await this.client.post('/tasks/bulk-update', { ids, updates })
    return data
  }

  async bulkAssignTasks(
    ids: string[],
    owner_id: string,
    notify_owner = true
  ): Promise<{ success: boolean; count: number; owner_id: string }> {
    const { data } = await this.client.post('/tasks/bulk-assign', { ids, owner_id, notify_owner })
    return data
  }

  // Events
  async getEvents(params?: PaginationParams): Promise<ApiResponse<Event[]>> {
    const { data } = await this.client.get<ApiResponse<Event[]>>('/events', { params })
    return data
  }

  async getEventStats(): Promise<EventStats> {
    const { data } = await this.client.get<{ data: EventStats }>('/events/stats')
    return data.data
  }

  async getEventsCalendar(start: string, end: string): Promise<Event[]> {
    const { data } = await this.client.get<ApiResponse<Event[]>>('/events/calendar', {
      params: { start, end },
    })
    return data.data
  }

  async getEvent(id: string): Promise<Event> {
    const { data } = await this.client.get<ApiResponse<Event>>(`/events/${id}`)
    return data.data
  }

  async createEvent(event: Partial<Event>): Promise<Event> {
    const { data } = await this.client.post<ApiResponse<Event>>('/events', event)
    return data.data
  }

  async updateEvent(id: string, event: Partial<Event>): Promise<Event> {
    const { data } = await this.client.put<ApiResponse<Event>>(`/events/${id}`, event)
    return data.data
  }

  async deleteEvent(id: string): Promise<void> {
    await this.client.delete(`/events/${id}`)
  }

  // Event Attendees
  async getEventAttendees(eventId: string): Promise<EventAttendee[]> {
    const { data } = await this.client.get<ApiResponse<EventAttendee[]>>(
      `/events/${eventId}/attendees`
    )
    return data.data
  }

  async addEventAttendee(
    eventId: string,
    attendee: Partial<EventAttendee>
  ): Promise<EventAttendee> {
    const { data } = await this.client.post<ApiResponse<EventAttendee>>(
      `/events/${eventId}/attendees`,
      attendee
    )
    return data.data
  }

  async removeEventAttendee(eventId: string, relationId: string): Promise<void> {
    await this.client.delete(`/events/${eventId}/attendees/${relationId}`)
  }

  // Task Relations
  async addTaskRelation(
    taskId: string,
    data: { relation_id: string }
  ): Promise<{ id: string; task_id: string; relation_id: string }> {
    const { data: resp } = await this.client.post<
      ApiResponse<{ id: string; task_id: string; relation_id: string }>
    >(`/tasks/${taskId}/relations`, data)
    return resp.data
  }

  async removeTaskRelation(taskId: string, relationId: string): Promise<void> {
    await this.client.delete(`/tasks/${taskId}/relations/${relationId}`)
  }

  async setTaskPrimaryContact(taskId: string, relationId: string | null): Promise<void> {
    await this.client.put(`/tasks/${taskId}/primary-contact`, { relation_id: relationId })
  }

  // Users
  async getUsers(params?: PaginationParams): Promise<ApiResponse<User[]>> {
    const { data } = await this.client.get<ApiResponse<User[]>>('/users', { params })
    return data
  }

  async getUser(id: string): Promise<User> {
    const { data } = await this.client.get<ApiResponse<User>>(`/users/${id}`)
    return data.data
  }

  async createUser(userData: Partial<User>): Promise<User> {
    const { data } = await this.client.post<ApiResponse<User>>('/users', userData)
    return data.data
  }

  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    const { data } = await this.client.put<ApiResponse<User>>(`/users/${id}`, userData)
    return data.data
  }

  async updateUserRole(id: string, user_type: UserType): Promise<User> {
    const { data } = await this.client.patch<ApiResponse<User>>(`/users/${id}/role`, { user_type })
    return data.data
  }

  async deleteUser(id: string): Promise<void> {
    await this.client.delete(`/users/${id}`)
  }

  async activateUser(id: string): Promise<User> {
    const { data } = await this.client.patch<ApiResponse<User>>(`/users/${id}/activate`)
    return data.data
  }

  async deleteUserPermanent(
    id: string
  ): Promise<{ message: string; data: { id: string; name: string; email: string } }> {
    const { data } = await this.client.delete<{
      message: string
      data: { id: string; name: string; email: string }
    }>(`/users/${id}/permanent`)
    return data
  }

  async bulkUpdateUsers(
    ids: string[],
    updates: Record<string, any>
  ): Promise<{ success: boolean; count: number }> {
    const { data } = await this.client.post<{ success: boolean; count: number }>(
      '/users/bulk-update',
      { ids, updates }
    )
    return data
  }

  async bulkDeleteUsers(ids: string[]): Promise<{ success: boolean; count: number }> {
    const { data } = await this.client.post<{ success: boolean; count: number }>(
      '/users/bulk-delete',
      { ids }
    )
    return data
  }

  // Impersonation
  async startImpersonation(targetUserId: string): Promise<{ success: boolean; targetUser: User }> {
    const { data } = await this.client.post<{ success: boolean; targetUser: User }>(
      '/impersonation/start',
      { targetUserId }
    )
    return data
  }

  async endImpersonation(): Promise<{ success: boolean }> {
    const { data } = await this.client.post<{ success: boolean }>('/impersonation/end')
    return data
  }

  async getImpersonatableUsers(
    params?: PaginationParams & { search?: string }
  ): Promise<ApiResponse<User[]>> {
    const { data } = await this.client.get<ApiResponse<User[]>>('/impersonation/users', { params })
    return data
  }

  // Call Logs
  async getCallLogs(params?: PaginationParams): Promise<ApiResponse<CallLog[]>> {
    const { data } = await this.client.get<ApiResponse<CallLog[]>>('/call-logs', { params })
    return data
  }

  async getCallLogStats(params?: { owner_id?: string }): Promise<CallLogStats> {
    const { data } = await this.client.get<{ data: CallLogStats }>('/call-logs/stats', { params })
    return data.data
  }

  async getCallLog(id: string): Promise<CallLog> {
    const { data } = await this.client.get<ApiResponse<CallLog>>(`/call-logs/${id}`)
    return data.data
  }

  async createCallLog(callLog: Partial<CallLog>): Promise<CallLog> {
    const { data } = await this.client.post<ApiResponse<CallLog>>('/call-logs', callLog)
    return data.data
  }

  async updateCallLog(id: string, callLog: Partial<CallLog>): Promise<CallLog> {
    const { data } = await this.client.put<ApiResponse<CallLog>>(`/call-logs/${id}`, callLog)
    return data.data
  }

  async deleteCallLog(id: string): Promise<void> {
    await this.client.delete(`/call-logs/${id}`)
  }

  async bulkDeleteCallLogs(ids: string[]): Promise<{ success: boolean; count: number }> {
    const { data } = await this.client.post('/call-logs/bulk-delete', { ids })
    return data
  }

  async getCallLogsByContact(
    id: string,
    params?: PaginationParams
  ): Promise<ApiResponse<CallLog[]>> {
    const { data } = await this.client.get<ApiResponse<CallLog[]>>(`/call-logs/by-contact/${id}`, {
      params,
    })
    return data
  }

  async getCallLogsByAccount(
    id: string,
    params?: PaginationParams
  ): Promise<ApiResponse<CallLog[]>> {
    const { data } = await this.client.get<ApiResponse<CallLog[]>>(`/call-logs/by-account/${id}`, {
      params,
    })
    return data
  }

  async getCallLogsByLead(id: string, params?: PaginationParams): Promise<ApiResponse<CallLog[]>> {
    const { data } = await this.client.get<ApiResponse<CallLog[]>>(`/call-logs/by-lead/${id}`, {
      params,
    })
    return data
  }

  async phoneLookup(number: string): Promise<PhoneLookupResult> {
    const { data } = await this.client.get<{ data: PhoneLookupResult }>('/call-logs/phone-lookup', {
      params: { number },
    })
    return data.data
  }

  // Activities (Timeline)
  async getActivityTimeline(params: ActivityTimelineParams): Promise<ActivityTimelineResponse> {
    const queryParams = new URLSearchParams({
      entity_id: params.entity_id,
      entity_type: params.entity_type,
    })

    if (params.page) queryParams.set('page', params.page.toString())
    if (params.limit) queryParams.set('limit', params.limit.toString())
    if (params.start_date) queryParams.set('start_date', params.start_date)
    if (params.end_date) queryParams.set('end_date', params.end_date)
    if (params.activity_types && params.activity_types.length > 0) {
      queryParams.set('activity_types', params.activity_types.join(','))
    }

    const { data } = await this.client.get<ActivityTimelineResponse>(
      `/activities/timeline?${queryParams.toString()}`
    )
    return data
  }

  async updateActivity(
    type: ActivityType,
    id: string,
    updates: Partial<Activity>
  ): Promise<Activity> {
    const { data } = await this.client.put<{ data: Activity }>(`/activities/${type}/${id}`, updates)
    return data.data
  }

  async deleteActivity(type: ActivityType, id: string): Promise<void> {
    await this.client.delete(`/activities/${type}/${id}`)
  }

  // Quotes
  async getQuotes(params?: PaginationParams): Promise<ApiResponse<Quote[]>> {
    const { data } = await this.client.get<ApiResponse<Quote[]>>('/quotes', { params })
    return data
  }

  async getQuote(id: string): Promise<Quote> {
    const { data } = await this.client.get<ApiResponse<Quote>>(`/quotes/${id}`)
    return data.data
  }

  async createQuote(quote: Partial<Quote>): Promise<Quote> {
    const { data } = await this.client.post<ApiResponse<Quote>>('/quotes', quote)
    return data.data
  }

  async updateQuote(id: string, quote: Partial<Quote>): Promise<Quote> {
    const { data } = await this.client.put<ApiResponse<Quote>>(`/quotes/${id}`, quote)
    return data.data
  }

  async deleteQuote(id: string): Promise<void> {
    await this.client.delete(`/quotes/${id}`)
  }

  async bulkDeleteQuotes(ids: string[]): Promise<{ success: boolean; count: number }> {
    const { data } = await this.client.post('/quotes/bulk-delete', { ids })
    return data
  }

  async bulkUpdateQuotes(
    ids: string[],
    updates: Record<string, any>
  ): Promise<{ success: boolean; count: number; updates: Record<string, any> }> {
    const { data } = await this.client.post('/quotes/bulk-update', { ids, updates })
    return data
  }

  async downloadQuotePdf(
    id: string,
    template?: string
  ): Promise<{ blob: Blob; engine: string | null; fallback: boolean }> {
    let response
    try {
      response = await this.client.get<Blob>(`/quotes/${id}/pdf`, {
        responseType: 'blob',
        params: template ? { template } : undefined,
      })
    } catch (error) {
      // Because the request uses responseType:'blob', an error response body
      // arrives as a Blob rather than parsed JSON — so the backend's helpful
      // message (e.g. "discount awaiting approval") would otherwise be lost.
      // Read and parse it, and rethrow a clean Error carrying that message.
      const data = (error as { response?: { data?: unknown } })?.response?.data
      if (data instanceof Blob) {
        let backendMessage: string | null = null
        try {
          const parsed = JSON.parse(await data.text())
          backendMessage = typeof parsed?.error === 'string' ? parsed.error : null
        } catch {
          backendMessage = null
        }
        if (backendMessage) throw new Error(backendMessage)
      }
      throw error
    }
    // The backend reports which engine rendered and whether the accessible
    // composer engine fell back to the legacy PDFKit one.
    const engine = (response.headers['x-quote-pdf-engine'] as string) ?? null
    const fallback = response.headers['x-quote-pdf-fallback'] === 'true'
    return { blob: response.data, engine, fallback }
  }

  async cloneQuote(id: string): Promise<Quote> {
    const { data } = await this.client.post<ApiResponse<Quote>>(`/quotes/${id}/clone`)
    return data.data
  }

  // Quote Line Items
  async getQuoteLineItems(quoteId: string): Promise<QuoteLineItem[]> {
    const { data } = await this.client.get<ApiResponse<QuoteLineItem[]>>(
      `/quotes/${quoteId}/line-items`
    )
    return data.data
  }

  async createQuoteLineItem(
    quoteId: string,
    lineItem: Partial<QuoteLineItem>
  ): Promise<QuoteLineItem> {
    const { data } = await this.client.post<ApiResponse<QuoteLineItem>>(
      `/quotes/${quoteId}/line-items`,
      lineItem
    )
    return data.data
  }

  async updateQuoteLineItem(
    quoteId: string,
    id: string,
    lineItem: Partial<QuoteLineItem>
  ): Promise<QuoteLineItem> {
    const { data } = await this.client.put<ApiResponse<QuoteLineItem>>(
      `/quotes/${quoteId}/line-items/${id}`,
      lineItem
    )
    return data.data
  }

  async deleteQuoteLineItem(quoteId: string, id: string): Promise<void> {
    await this.client.delete(`/quotes/${quoteId}/line-items/${id}`)
  }

  // Opportunity Line Items
  async getOpportunityLineItems(opportunityId: string): Promise<OpportunityLineItem[]> {
    const { data } = await this.client.get<ApiResponse<OpportunityLineItem[]>>(
      `/opportunities/${opportunityId}/line-items`
    )
    return data.data
  }

  async createOpportunityLineItem(
    opportunityId: string,
    lineItem: Partial<OpportunityLineItem>
  ): Promise<OpportunityLineItem> {
    const { data } = await this.client.post<ApiResponse<OpportunityLineItem>>(
      `/opportunities/${opportunityId}/line-items`,
      lineItem
    )
    return data.data
  }

  async updateOpportunityLineItem(
    opportunityId: string,
    id: string,
    lineItem: Partial<OpportunityLineItem>
  ): Promise<OpportunityLineItem> {
    const { data } = await this.client.put<ApiResponse<OpportunityLineItem>>(
      `/opportunities/${opportunityId}/line-items/${id}`,
      lineItem
    )
    return data.data
  }

  async deleteOpportunityLineItem(opportunityId: string, id: string): Promise<void> {
    await this.client.delete(`/opportunities/${opportunityId}/line-items/${id}`)
  }

  async reorderOpportunityLineItems(
    opportunityId: string,
    order: { id: string; sort_order: number }[]
  ): Promise<void> {
    await this.client.put(`/opportunities/${opportunityId}/line-items/reorder`, { order })
  }

  /**
   * Approve or reject a line item discount. The server decides whether an
   * "approve" is a first-pass endorsement (>10%, acting Admin) or a final
   * approval, and returns the resulting status.
   */
  async decideOpportunityLineItemDiscount(
    opportunityId: string,
    id: string,
    action: 'approve' | 'reject'
  ): Promise<DiscountDecisionResult> {
    const { data } = await this.client.put<ApiResponse<DiscountDecisionResult>>(
      `/opportunities/${opportunityId}/line-items/${id}/approve`,
      { action }
    )
    return data.data
  }

  /** Reverse a discount decision, returning the line to 'pending'. */
  async revertOpportunityLineItemDiscount(
    opportunityId: string,
    id: string,
    reason?: string
  ): Promise<DiscountDecisionResult> {
    const { data } = await this.client.put<ApiResponse<DiscountDecisionResult>>(
      `/opportunities/${opportunityId}/line-items/${id}/revert-approval`,
      { reason }
    )
    return data.data
  }

  // Opportunity Contact Roles
  async getOpportunityContactRoles(
    opportunityId: string,
    params?: { role?: string }
  ): Promise<OpportunityContactRole[]> {
    const { data } = await this.client.get<ApiResponse<OpportunityContactRole[]>>(
      `/opportunities/${opportunityId}/contact-roles`,
      { params }
    )
    return data.data
  }

  async createOpportunityContactRole(
    opportunityId: string,
    contactRole: Partial<OpportunityContactRole>
  ): Promise<OpportunityContactRole> {
    const { data } = await this.client.post<ApiResponse<OpportunityContactRole>>(
      `/opportunities/${opportunityId}/contact-roles`,
      contactRole
    )
    return data.data
  }

  async updateOpportunityContactRole(
    opportunityId: string,
    id: string,
    contactRole: Partial<OpportunityContactRole>
  ): Promise<OpportunityContactRole> {
    const { data } = await this.client.put<ApiResponse<OpportunityContactRole>>(
      `/opportunities/${opportunityId}/contact-roles/${id}`,
      contactRole
    )
    return data.data
  }

  async deleteOpportunityContactRole(opportunityId: string, id: string): Promise<void> {
    await this.client.delete(`/opportunities/${opportunityId}/contact-roles/${id}`)
  }

  /**
   * Pricing governance switches. Readable by any authenticated user because the
   * selling pickers need it to decide whether to restrict themselves.
   */
  async getPricingSettings(): Promise<
    ApiResponse<{ require_priced_products: { enabled: boolean } }>
  > {
    const { data } =
      await this.client.get<ApiResponse<{ require_priced_products: { enabled: boolean } }>>(
        '/admin/pricing-settings'
      )
    return data
  }

  async setRequirePricedProducts(
    enabled: boolean
  ): Promise<ApiResponse<{ require_priced_products: { enabled: boolean } }>> {
    const { data } = await this.client.put<
      ApiResponse<{ require_priced_products: { enabled: boolean } }>
    >('/admin/pricing-settings', { require_priced_products: enabled })
    return data
  }

  // ---- Account name aliases ------------------------------------------------
  async getUnresolvedAliases(
    source?: string
  ): Promise<{ data: import('@/types').UnresolvedAlias[]; total: number }> {
    const { data } = await this.client.get('/account-aliases/unresolved', {
      params: source ? { source } : undefined,
    })
    return data
  }

  async getAccountSuggestions(
    name: string,
    limit = 8
  ): Promise<{ data: import('@/types').AccountSuggestion[] }> {
    const { data } = await this.client.get('/account-aliases/suggestions', {
      params: { name, limit },
    })
    return data
  }

  async resolveAccountAlias(
    id: number,
    accountId: string
  ): Promise<{ data: import('@/types').UnresolvedAlias }> {
    const { data } = await this.client.patch(`/account-aliases/${id}`, {
      account_id: accountId,
    })
    return data
  }

  // ---- Training worklist ---------------------------------------------------
  async getTrainingSessions(params?: PaginationParams): Promise<
    ApiResponse<import('@/types').TrainingSession[]> & {
      stats?: import('@/types').TrainingSessionStats
    }
  > {
    const { data } = await this.client.get('/training/sessions', { params })
    return data
  }

  async getTrainingVocabularies(): Promise<{
    data: import('@/types').TrainingVocabularies
  }> {
    const { data } = await this.client.get('/training/vocabularies')
    return data
  }

  async getTrainers(): Promise<{ data: import('@/types').TrainerOption[] }> {
    const { data } = await this.client.get('/training/trainers')
    return data
  }

  async createTrainingSession(
    payload: Partial<import('@/types').TrainingSession> & {
      account_id: string
      course_name: string
    }
  ): Promise<ApiResponse<import('@/types').TrainingSession>> {
    const { data } = await this.client.post('/training/sessions', payload)
    return data
  }

  async getTrainingStatuses(): Promise<{ data: string[] }> {
    const { data } = await this.client.get<{ data: string[] }>('/training/statuses')
    return data
  }

  async updateTrainingSession(
    id: string,
    payload: Partial<import('@/types').TrainingSession>
  ): Promise<ApiResponse<import('@/types').TrainingSession>> {
    const { data } = await this.client.patch(`/training/sessions/${id}`, payload)
    return data
  }

  // ---- Importing the tracker workbook (Admin only) -------------------------
  //
  // Two steps: preview returns a token plus a report of exactly what would happen,
  // and nothing is written until that token is committed.
  async previewTrainingImport(
    file: File
  ): Promise<{ data: import('@/types').TrainingImportReport }> {
    const form = new FormData()
    form.append('file', file)
    // The header override is required, not optional. This client sets a default
    // Content-Type of application/json, so without it the body goes up labelled as
    // JSON, multer parses no file at all, and the server answers "No file was
    // uploaded" for a request that plainly had one. Every other upload here does the
    // same — see uploadContent and uploadCompanyDocument.
    const { data } = await this.client.post('/training/import/preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  }

  async commitTrainingImport(
    token: string,
    restoreDeleted: boolean
  ): Promise<{ data: import('@/types').TrainingImportResult }> {
    const { data } = await this.client.post('/training/import/commit', {
      token,
      restore_deleted: restoreDeleted,
    })
    return data
  }

  // ---- The other five tracker worksheets -----------------------------------
  //
  // One pair of methods for all five, matching the single generated route on the
  // backend. Rows are typed as a loose record: the five tabs share almost no
  // columns, and each page declares the ones it renders.
  async getTrainingWorksheet(
    sheet: import('@/types').TrainingWorksheetKey,
    params?: PaginationParams
  ): Promise<ApiResponse<import('@/types').TrainingWorksheetRow[]>> {
    const { data } = await this.client.get(`/training/worksheets/${sheet}`, { params })
    return data
  }

  async updateTrainingWorksheetRow(
    sheet: import('@/types').TrainingWorksheetKey,
    id: string,
    payload: Record<string, unknown>
  ): Promise<ApiResponse<import('@/types').TrainingWorksheetRow>> {
    const { data } = await this.client.patch(`/training/worksheets/${sheet}/${id}`, payload)
    return data
  }

  // Products
  async getProducts(params?: PaginationParams): Promise<ApiResponse<Product[]>> {
    const { data } = await this.client.get<ApiResponse<Product[]>>('/products', { params })
    return data
  }

  async getProduct(id: string): Promise<Product> {
    const { data } = await this.client.get<ApiResponse<Product>>(`/products/${id}`)
    return data.data
  }

  async createProduct(product: Partial<Product>): Promise<Product> {
    const { data } = await this.client.post<ApiResponse<Product>>('/products', product)
    return data.data
  }

  async updateProduct(id: string, product: Partial<Product>): Promise<Product> {
    const { data } = await this.client.put<ApiResponse<Product>>(`/products/${id}`, product)
    return data.data
  }

  async deleteProduct(id: string): Promise<void> {
    await this.client.delete(`/products/${id}`)
  }

  async bulkDeleteProducts(ids: string[]): Promise<{ success: boolean; count: number }> {
    const { data } = await this.client.post('/products/bulk-delete', { ids })
    return data
  }

  async bulkUpdateProducts(
    ids: string[],
    updates: Record<string, any>
  ): Promise<{ success: boolean; count: number; updates: Record<string, any> }> {
    const { data } = await this.client.post('/products/bulk-update', { ids, updates })
    return data
  }

  async updateProductPricing(
    id: string,
    pricing: { unit_price: number; currency_iso_code?: string }
  ): Promise<ApiResponse<any>> {
    const { data } = await this.client.put<ApiResponse<any>>(`/products/${id}/pricing`, pricing)
    return data
  }

  async deleteProductPricing(id: string): Promise<void> {
    await this.client.delete(`/products/${id}/pricing`)
  }

  // Pricebooks
  async getPricebooks(params?: PaginationParams): Promise<ApiResponse<Pricebook[]>> {
    const { data } = await this.client.get<ApiResponse<Pricebook[]>>('/pricebooks', { params })
    return data
  }

  async getPricebook(id: string): Promise<Pricebook> {
    const { data } = await this.client.get<ApiResponse<Pricebook>>(`/pricebooks/${id}`)
    return data.data
  }

  // Pricebook Entries
  async getPricebookEntries(
    pricebookId: string,
    params?: PaginationParams
  ): Promise<ApiResponse<PricebookEntry[]>> {
    const { data } = await this.client.get<ApiResponse<PricebookEntry[]>>(
      `/pricebooks/${pricebookId}/entries`,
      { params }
    )
    return data
  }

  // REMOVED: createPricebookEntry / updatePricebookEntry / deletePricebookEntry.
  // They called POST/PUT/DELETE `/pricebooks/:id/entries[/:entryId]`, none of
  // which exist on the backend — every call would have 404'd. Nothing invoked
  // them (the editor funnels all writes through bulkPatchPricebookEntries
  // below). Per-entry CRUD does exist at the top-level `/pricebook-entries`
  // routes if it is ever needed.
  //
  // bulkDeletePricebooks / bulkUpdatePricebooks are gone for the same reason,
  // together with the Pricebooks page buttons that called them.

  // ---- Price matrix: products down, governed currencies across -------------

  async getPriceMatrix(
    pricebookId: string,
    params?: { search?: string; only_gaps?: boolean; include_inactive?: boolean }
  ): Promise<import('@/types').PriceMatrix> {
    const { data } = await this.client.get<{ data: import('@/types').PriceMatrix }>(
      `/pricebooks/${pricebookId}/price-matrix`,
      { params }
    )
    return data.data
  }

  /** CSV of GOVERNED prices only — derived cells are exported blank on purpose. */
  async exportPriceMatrix(pricebookId: string, params?: { search?: string }): Promise<string> {
    const { data } = await this.client.get<string>(
      `/pricebooks/${pricebookId}/price-matrix/export`,
      { params, responseType: 'text' }
    )
    return data
  }

  /** Parses a CSV and reports what it WOULD do. Writes nothing. */
  async previewPriceMatrixImport(
    pricebookId: string,
    csv: string
  ): Promise<import('@/types').PriceMatrixImportPreview> {
    const { data } = await this.client.post<{
      data: import('@/types').PriceMatrixImportPreview
    }>(`/pricebooks/${pricebookId}/price-matrix/preview-import`, { csv })
    return data.data
  }

  // Bulk editor (PATCH creates + updates + deletes in one transaction,
  // returns a batch_id that can be reverted via the batches endpoint).
  async bulkPatchPricebookEntries(
    pricebookId: string,
    payload: import('@/types').PricebookEntryBulkPatch
  ): Promise<import('@/types').PricebookEntryBulkResult> {
    const { data } = await this.client.patch<{ data: import('@/types').PricebookEntryBulkResult }>(
      `/pricebooks/${pricebookId}/entries/bulk`,
      payload
    )
    return data.data
  }

  async listPricebookEntryBatches(
    pricebookId: string,
    limit = 10
  ): Promise<import('@/types').PricebookEntryBatch[]> {
    const { data } = await this.client.get<{ data: import('@/types').PricebookEntryBatch[] }>(
      `/pricebooks/${pricebookId}/entries/batches`,
      { params: { limit } }
    )
    return data.data
  }

  async revertPricebookEntryBatch(
    pricebookId: string,
    batchId: string,
    force = false
  ): Promise<import('@/types').PricebookBatchRevertResult> {
    const { data } = await this.client.post<{ data: import('@/types').PricebookBatchRevertResult }>(
      `/pricebooks/${pricebookId}/entries/batches/${batchId}/revert`,
      undefined,
      { params: force ? { force: 'true' } : undefined }
    )
    return data.data
  }

  // Orders
  async getOrders(params?: PaginationParams): Promise<ApiResponse<Order[]>> {
    const { data } = await this.client.get<ApiResponse<Order[]>>('/orders', { params })
    return data
  }

  // ------------------------------------------------------------ order email ---

  /**
   * Templates for the order email, grouped by category.
   *
   * order_id is passed so the API can float the templates matching this order's products
   * to the top of their group. There are 26 of them and they have similar names.
   */
  async getEmailTemplates(orderId?: string): Promise<EmailTemplateGroup[]> {
    const { data } = await this.client.get<ApiResponse<EmailTemplateGroup[]>>('/email-templates', {
      params: orderId ? { order_id: orderId } : undefined,
    })
    return data.data
  }

  /** Render a template against an order. Writes nothing. */
  async previewOrderEmail(orderId: string, templateId: string): Promise<OrderEmailPreview> {
    const { data } = await this.client.post<ApiResponse<OrderEmailPreview>>(
      `/orders/${orderId}/email/preview`,
      { template_id: templateId }
    )
    return data.data
  }

  /**
   * Send the order email.
   *
   * multipart, because attachments come with it. Unlike sendEmail() this accepts an
   * external recipient: the @grackledocs.com restriction on the general compose route is
   * deliberately still in place there.
   */
  async sendOrderEmail(
    orderId: string,
    payload: {
      /** Required: the server uses it to enforce the internal-template boundary. */
      template_id: string
      to_address: string
      cc_address?: string
      bcc_address?: string
      subject: string
      html_body: string
      text_body: string
      attachments?: File[]
    }
  ): Promise<{ data: { id: string; status: string }; warnings?: string[] }> {
    const form = new FormData()
    form.append('template_id', payload.template_id)
    form.append('to_address', payload.to_address)
    if (payload.cc_address) form.append('cc_address', payload.cc_address)
    if (payload.bcc_address) form.append('bcc_address', payload.bcc_address)
    form.append('subject', payload.subject)
    form.append('html_body', payload.html_body)
    form.append('text_body', payload.text_body)
    for (const file of payload.attachments ?? []) form.append('attachments', file)

    const { data } = await this.client.post<{
      data: { id: string; status: string }
      warnings?: string[]
    }>(`/orders/${orderId}/email`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Attachments plus a Gmail round trip. The default is not generous enough.
      timeout: 120000,
    })
    return data
  }

  async getOrder(id: string): Promise<Order> {
    const { data } = await this.client.get<ApiResponse<Order>>(`/orders/${id}`)
    return data.data
  }

  async getOrderStats(): Promise<OrderStats> {
    const { data } = await this.client.get<ApiResponse<OrderStats>>('/orders/stats')
    return data.data
  }

  async getOrderItems(orderId: string): Promise<OrderItem[]> {
    const { data } = await this.client.get<ApiResponse<OrderItem[]>>(`/orders/${orderId}/items`)
    return data.data
  }

  async createOrder(order: Partial<Order>): Promise<Order> {
    const { data } = await this.client.post<ApiResponse<Order>>('/orders', order)
    return data.data
  }

  async updateOrder(id: string, order: Partial<Order>): Promise<Order> {
    const { data } = await this.client.put<ApiResponse<Order>>(`/orders/${id}`, order)
    return data.data
  }

  async deleteOrder(id: string): Promise<void> {
    await this.client.delete(`/orders/${id}`)
  }

  async bulkUpdateOrders(
    ids: string[],
    updates: Record<string, any>
  ): Promise<{ success: boolean; count: number; updates: Record<string, any> }> {
    const { data } = await this.client.post('/orders/bulk-update', { ids, updates })
    return data
  }

  // Emails
  async getEmails(
    params?: PaginationParams & {
      incoming?: boolean
      has_attachment?: boolean
      status?: string
      from_date?: string
      to_date?: string
    }
  ): Promise<ApiResponse<Email[]>> {
    const { data } = await this.client.get<ApiResponse<Email[]>>('/emails', { params })
    return data
  }

  async getEmail(id: string): Promise<Email> {
    const { data } = await this.client.get<ApiResponse<Email>>(`/emails/${id}`)
    return data.data
  }

  async getEmailThread(id: string): Promise<Email[]> {
    const { data } = await this.client.get<ApiResponse<Email[]>>(`/emails/${id}/thread`)
    return data.data
  }

  async getEmailStats(): Promise<EmailStats> {
    const { data } = await this.client.get<ApiResponse<EmailStats>>('/emails/stats')
    return data.data
  }

  async sendEmail(email: Partial<Email>): Promise<Email & { warning?: string }> {
    const { data } = await this.client.post<ApiResponse<Email> & { warning?: string }>(
      '/emails',
      email
    )
    const result = data.data as Email & { warning?: string }
    if (data.warning) result.warning = data.warning
    return result
  }

  async updateEmail(id: string, email: Partial<Email>): Promise<Email> {
    const { data } = await this.client.put<ApiResponse<Email>>(`/emails/${id}`, email)
    return data.data
  }

  async deleteEmail(id: string): Promise<void> {
    await this.client.delete(`/emails/${id}`)
  }

  async getEmailsByContact(
    contactId: string,
    params?: PaginationParams
  ): Promise<ApiResponse<Email[]>> {
    const { data } = await this.client.get<ApiResponse<Email[]>>(
      `/emails/by-contact/${contactId}`,
      { params }
    )
    return data
  }

  async getEmailsByAccount(
    accountId: string,
    params?: PaginationParams
  ): Promise<ApiResponse<Email[]>> {
    const { data } = await this.client.get<ApiResponse<Email[]>>(
      `/emails/by-account/${accountId}`,
      { params }
    )
    return data
  }

  async getEmailsByOpportunity(
    opportunityId: string,
    params?: PaginationParams
  ): Promise<ApiResponse<Email[]>> {
    const { data } = await this.client.get<ApiResponse<Email[]>>(
      `/emails/by-opportunity/${opportunityId}`,
      { params }
    )
    return data
  }

  async getEmailsByLead(leadId: string, params?: PaginationParams): Promise<ApiResponse<Email[]>> {
    const { data } = await this.client.get<ApiResponse<Email[]>>(`/emails/by-lead/${leadId}`, {
      params,
    })
    return data
  }

  // History
  async getEntityHistory(params: HistoryTimelineParams): Promise<HistoryTimelineResponse> {
    const queryParams = new URLSearchParams()

    if (params.page) queryParams.set('page', params.page.toString())
    if (params.limit) queryParams.set('limit', params.limit.toString())
    if (params.start_date) queryParams.set('start_date', params.start_date)
    if (params.end_date) queryParams.set('end_date', params.end_date)
    if (params.field_names) queryParams.set('field_names', params.field_names)
    if (params.changed_by) queryParams.set('changed_by', params.changed_by)

    const { data } = await this.client.get<HistoryTimelineResponse>(
      `/history/entity/${params.entity_id}?${queryParams}`
    )
    return data
  }

  async getOpportunityHistory(
    params: OpportunityHistoryParams
  ): Promise<OpportunityHistoryResponse> {
    const queryParams = new URLSearchParams()

    if (params.page) queryParams.set('page', params.page.toString())
    if (params.limit) queryParams.set('limit', params.limit.toString())
    if (params.start_date) queryParams.set('start_date', params.start_date)
    if (params.end_date) queryParams.set('end_date', params.end_date)

    const { data } = await this.client.get<OpportunityHistoryResponse>(
      `/history/opportunity/${params.opportunity_id}?${queryParams}`
    )
    return data
  }

  async getHistoryStats(
    entityId: string,
    range?: { start_date?: string; end_date?: string }
  ): Promise<HistoryStats> {
    const queryParams = new URLSearchParams()
    if (range?.start_date) queryParams.set('start_date', range.start_date)
    if (range?.end_date) queryParams.set('end_date', range.end_date)
    const qs = queryParams.toString()
    const { data } = await this.client.get<HistoryStats>(
      `/history/stats/${entityId}${qs ? `?${qs}` : ''}`
    )
    return data
  }

  // Content/Attachments
  async getContent(params?: ContentQueryParams): Promise<ApiResponse<ContentVersion[]>> {
    const queryParams = new URLSearchParams()

    if (params?.search) queryParams.set('search', params.search)
    if (params?.page) queryParams.set('page', params.page.toString())
    if (params?.limit) queryParams.set('limit', params.limit.toString())
    if (params?.file_type) queryParams.set('file_type', params.file_type)
    if (params?.linked_entity_id) queryParams.set('linked_entity_id', params.linked_entity_id)
    if (params?.owner_id) queryParams.set('owner_id', params.owner_id)
    if (params?.start_date) queryParams.set('start_date', params.start_date)
    if (params?.end_date) queryParams.set('end_date', params.end_date)

    const { data } = await this.client.get<ApiResponse<ContentVersion[]>>(`/content?${queryParams}`)
    return data
  }

  async getContentStats(): Promise<ContentStats> {
    const { data } = await this.client.get<ContentStats>('/content/stats')
    return data
  }

  async getContentById(id: string): Promise<{ data: ContentVersion }> {
    const { data } = await this.client.get<{ data: ContentVersion }>(`/content/${id}`)
    return data
  }

  async getEntityContent(
    entityId: string,
    params?: PaginationParams
  ): Promise<ApiResponse<ContentVersion[]>> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.set('page', params.page.toString())
    if (params?.limit) queryParams.set('limit', params.limit.toString())

    const { data } = await this.client.get<ApiResponse<ContentVersion[]>>(
      `/content/entity/${entityId}?${queryParams}`
    )
    return data
  }

  async uploadContent(formData: FormData): Promise<{ data: ContentVersion }> {
    const { data } = await this.client.post<{ data: ContentVersion }>('/content/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return data
  }

  async downloadContent(id: string): Promise<Blob> {
    const { data } = await this.client.get(`/content/${id}/download`, {
      responseType: 'blob',
    })
    return data
  }

  async getContentPreview(id: string): Promise<ContentPreviewResponse> {
    const { data } = await this.client.get<ContentPreviewResponse>(`/content/${id}/preview`)
    return data
  }

  // Streams the cached thumbnail and returns the raw Blob. The caller is
  // responsible for creating + revoking object URLs from it -- caching the
  // Blob (rather than a URL) lets multiple component lifecycles each derive
  // their own URL. Returns null on 204 No Content.
  async getContentThumbnail(id: string): Promise<{ blob: Blob } | null> {
    const response = await this.client.get<Blob>(`/content/${id}/thumbnail`, {
      responseType: 'blob',
      validateStatus: (status) => status === 200 || status === 204,
    })
    if (response.status === 204 || !response.data || (response.data as Blob).size === 0) {
      return null
    }
    return { blob: response.data }
  }

  async updateContent(
    id: string,
    updateData: UpdateContentData
  ): Promise<{ data: ContentVersion }> {
    const { data } = await this.client.put<{ data: ContentVersion }>(`/content/${id}`, updateData)
    return data
  }

  async deleteContent(id: string): Promise<{ message: string }> {
    const { data } = await this.client.delete<{ message: string }>(`/content/${id}`)
    return data
  }

  async linkContent(id: string, linkData: LinkContentData): Promise<{ data: unknown }> {
    const { data } = await this.client.post(`/content/${id}/link`, linkData)
    return data
  }

  async unlinkContent(id: string, linkedEntityId: string): Promise<{ message: string }> {
    const { data } = await this.client.delete<{ message: string }>(
      `/content/${id}/unlink/${linkedEntityId}`
    )
    return data
  }

  // Repository (curated document library)
  async getRepositoryAssets(params?: {
    section?: import('@/types').RepositorySection
    asset_type?: string
    product_id?: string
    tag?: string
    search?: string
    page?: number
    limit?: number
  }): Promise<ApiResponse<import('@/types').RepositoryAsset[]>> {
    const { data } = await this.client.get<ApiResponse<import('@/types').RepositoryAsset[]>>(
      '/repository',
      { params }
    )
    return data
  }

  async getRepositoryAsset(id: string): Promise<{ data: import('@/types').RepositoryAsset }> {
    const { data } = await this.client.get<{ data: import('@/types').RepositoryAsset }>(
      `/repository/${id}`
    )
    return data
  }

  async promoteToRepository(
    contentVersionId: string,
    payload: {
      repository_section: import('@/types').RepositorySection
      asset_type: string
      repository_summary?: string
      tags?: string[]
    }
  ): Promise<{ data: import('@/types').RepositoryAsset }> {
    const { data } = await this.client.post<{ data: import('@/types').RepositoryAsset }>(
      `/repository/promote/${contentVersionId}`,
      payload
    )
    return data
  }

  async updateRepositoryAsset(
    id: string,
    payload: Partial<{
      repository_section: import('@/types').RepositorySection
      asset_type: string
      repository_summary: string | null
      tags: string[]
    }>
  ): Promise<{ data: import('@/types').RepositoryAsset }> {
    const { data } = await this.client.put<{ data: import('@/types').RepositoryAsset }>(
      `/repository/${id}`,
      payload
    )
    return data
  }

  async demoteRepositoryAsset(id: string): Promise<{ success: boolean }> {
    const { data } = await this.client.delete<{ success: boolean }>(`/repository/${id}`)
    return data
  }

  // ----- Marketing repository ------------------------------------------------
  async getMarketingTagCategories(): Promise<import('@/types').MarketingTagCategory[]> {
    const r = await this.client.get<{ data: import('@/types').MarketingTagCategory[] }>(
      '/marketing/tag-categories'
    )
    return r.data.data
  }

  async createMarketingTagValue(payload: {
    category_key: string
    value: string
  }): Promise<import('@/types').MarketingTagValue & { category_key: string }> {
    const r = await this.client.post<{
      data: import('@/types').MarketingTagValue & { category_key: string }
    }>('/marketing/tag-values', payload)
    return r.data.data
  }

  async deleteMarketingTagValue(
    id: string,
    confirm = false
  ): Promise<{ success: boolean; removedFrom?: number }> {
    const { data } = await this.client.delete<{ success: boolean; removedFrom?: number }>(
      `/marketing/tag-values/${id}${confirm ? '?confirm=true' : ''}`
    )
    return data
  }

  async getMarketingAssets(params?: {
    q?: string
    tagValueIds?: string[]
    sort?: 'recent' | 'downloads'
    page?: number
    limit?: number
  }): Promise<import('@/types').ApiResponse<import('@/types').MarketingAsset[]>> {
    const query: Record<string, string | number> = {}
    if (params?.q) query.q = params.q
    if (params?.tagValueIds?.length) query.tagValueIds = params.tagValueIds.join(',')
    if (params?.sort) query.sort = params.sort
    if (params?.page) query.page = params.page
    if (params?.limit) query.limit = params.limit
    const { data } = await this.client.get(`/marketing/assets`, { params: query })
    return data
  }

  async createMarketingAsset(payload: {
    contentVersionId: string
    title?: string
    description?: string
    tagValueIds: string[]
  }): Promise<{ data: { id: string } }> {
    const { data } = await this.client.post<{ data: { id: string } }>('/marketing/assets', payload)
    return data
  }

  async updateMarketingAsset(
    id: string,
    payload: { title?: string; description?: string | null; tagValueIds?: string[] }
  ): Promise<{ success: boolean }> {
    const { data } = await this.client.put<{ success: boolean }>(`/marketing/assets/${id}`, payload)
    return data
  }

  async deleteMarketingAsset(id: string): Promise<{ success: boolean }> {
    const { data } = await this.client.delete<{ success: boolean }>(`/marketing/assets/${id}`)
    return data
  }

  async linkRepositoryAssetToProduct(
    id: string,
    payload: { product_id: string; role: string; sort_order?: number }
  ): Promise<{ data: unknown }> {
    const { data } = await this.client.post<{ data: unknown }>(
      `/repository/${id}/products`,
      payload
    )
    return data
  }

  async unlinkRepositoryAssetFromProduct(
    id: string,
    linkId: string
  ): Promise<{ success: boolean }> {
    const { data } = await this.client.delete<{ success: boolean }>(
      `/repository/${id}/products/${linkId}`
    )
    return data
  }

  // Product documents (per-product view of the repository)
  async getProductDocuments(
    productId: string
  ): Promise<{ data: import('@/types').ProductDocument[] }> {
    const { data } = await this.client.get<{ data: import('@/types').ProductDocument[] }>(
      `/products/${productId}/documents`
    )
    return data
  }

  async addProductDocument(
    productId: string,
    payload: { content_document_id: string; role: string; sort_order?: number }
  ): Promise<{ data: unknown }> {
    const { data } = await this.client.post<{ data: unknown }>(
      `/products/${productId}/documents`,
      payload
    )
    return data
  }

  async removeProductDocument(productId: string, linkId: string): Promise<{ success: boolean }> {
    const { data } = await this.client.delete<{ success: boolean }>(
      `/products/${productId}/documents/${linkId}`
    )
    return data
  }

  // Notes
  async getNotes(params: { parent_id: string }): Promise<ApiResponse<Note[]>> {
    const { data } = await this.client.get<ApiResponse<Note[]>>('/notes', { params })
    return data
  }

  async getNote(id: string): Promise<Note> {
    const { data } = await this.client.get<ApiResponse<Note>>(`/notes/${id}`)
    return data.data
  }

  async createNote(note: {
    parent_id: string
    title?: string
    body: string
    is_private?: boolean
    owner_id?: string
  }): Promise<Note> {
    const { data } = await this.client.post<ApiResponse<Note>>('/notes', note)
    return data.data
  }

  async updateNote(id: string, data: Partial<Note>): Promise<Note> {
    const { data: response } = await this.client.put<ApiResponse<Note>>(`/notes/${id}`, data)
    return response.data
  }

  async deleteNote(id: string): Promise<void> {
    await this.client.delete(`/notes/${id}`)
  }

  // Reports
  async getSalesReport(params?: { from?: string; to?: string }): Promise<unknown> {
    const { data } = await this.client.get('/reports/sales', { params })
    return data.data
  }

  async getLeadReport(params?: { from?: string; to?: string }): Promise<unknown> {
    const { data } = await this.client.get('/reports/leads', { params })
    return data.data
  }

  async getLeadSourceAnalysis(params?: ReportQueryParams): Promise<LeadSourceAnalysisData> {
    const { data } = await this.client.get<ApiResponse<LeadSourceAnalysisData>>(
      '/reports/lead-source-analysis',
      { params }
    )
    return data.data
  }

  async getSalesForecast(params?: ReportQueryParams): Promise<SalesForecastData> {
    const { data } = await this.client.get<ApiResponse<SalesForecastData>>(
      '/reports/sales-forecast',
      { params }
    )
    return data.data
  }

  async getRevenueMetrics(params?: ReportQueryParams): Promise<RevenueMetric[]> {
    const { data } = await this.client.get<ApiResponse<RevenueMetric[]>>(
      '/reports/revenue-metrics',
      { params }
    )
    return data.data
  }

  async getPipelineByStage(params?: {
    owner_id?: string
    startDate?: string
    endDate?: string
  }): Promise<PipelineStageMetric[]> {
    const { data } = await this.client.get<ApiResponse<PipelineStageMetric[]>>(
      '/reports/pipeline-by-stage',
      { params }
    )
    return data.data
  }

  async getDaysToClose(params?: ReportQueryParams): Promise<any> {
    const { data } = await this.client.get<ApiResponse<any>>('/reports/days-to-close', { params })
    return data.data
  }

  async getWinLossAnalysis(params?: ReportQueryParams): Promise<any> {
    const { data } = await this.client.get<ApiResponse<any>>('/reports/win-loss-analysis', {
      params,
    })
    return data.data
  }

  async getTopAccounts(params?: {
    limit?: number
    sortBy?: string
    owner_id?: string
  }): Promise<any> {
    const { data } = await this.client.get<ApiResponse<any>>('/reports/top-accounts', { params })
    return data.data
  }

  async getBigOpportunities(params?: {
    limit?: number
    minAmount?: number
    status?: string
    owner_id?: string
  }): Promise<any> {
    const { data } = await this.client.get<ApiResponse<any>>('/reports/big-opportunities', {
      params,
    })
    return data.data
  }

  async getStuckOpportunities(params?: {
    owner_id?: string
    stage_name?: string
    min_tier?: string
    sort?: string
    sort_dir?: string
  }): Promise<import('@/types').StuckOpportunitiesData> {
    const { data } = await this.client.get<ApiResponse<import('@/types').StuckOpportunitiesData>>(
      '/reports/stuck-opportunities',
      { params }
    )
    return data.data
  }

  async getMarketingSingleMonth(params?: {
    month?: string
    owner_id?: string
  }): Promise<import('@/types').MarketingSingleMonthData> {
    const { data } = await this.client.get<ApiResponse<import('@/types').MarketingSingleMonthData>>(
      '/reports/marketing/single-month',
      { params }
    )
    return data.data
  }

  async getMarketingByMonth(params?: {
    startDate?: string
    endDate?: string
    status?: 'pipeline' | 'won'
    owner_id?: string
  }): Promise<import('@/types').MarketingByMonthData> {
    const { data } = await this.client.get<ApiResponse<import('@/types').MarketingByMonthData>>(
      '/reports/marketing/by-month',
      { params }
    )
    return data.data
  }

  async getMarketingFunnel(params?: {
    startDate?: string
    endDate?: string
    owner_id?: string
  }): Promise<import('@/types').MarketingFunnelData> {
    const { data } = await this.client.get<ApiResponse<import('@/types').MarketingFunnelData>>(
      '/reports/marketing/funnel',
      { params }
    )
    return data.data
  }

  async getMarketingRevenueFunnel(params?: {
    startDate?: string
    endDate?: string
    owner_id?: string
  }): Promise<import('@/types').MarketingRevenueFunnelData> {
    const { data } = await this.client.get<
      ApiResponse<import('@/types').MarketingRevenueFunnelData>
    >('/reports/marketing/revenue-funnel', { params })
    return data.data
  }

  async getProductByRevenue(params?: {
    startDate?: string
    endDate?: string
    owner_id?: string
    compare?: boolean
  }): Promise<import('@/types').ProductRevenueData> {
    const { data } = await this.client.get<ApiResponse<import('@/types').ProductRevenueData>>(
      '/reports/product-by-revenue',
      { params }
    )
    return data.data
  }

  async getProductRevenueTrend(params?: {
    startDate?: string
    endDate?: string
    owner_id?: string
  }): Promise<import('@/types').ProductRevenueTrendData> {
    const { data } = await this.client.get<ApiResponse<import('@/types').ProductRevenueTrendData>>(
      '/reports/product-by-revenue/trend',
      { params }
    )
    return data.data
  }

  async getTtmRevenue(params: {
    category: 'Software' | 'Services'
    owner_id?: string
  }): Promise<import('@/types').TtmRevenueData> {
    const { data } = await this.client.get<ApiResponse<import('@/types').TtmRevenueData>>(
      '/reports/ttm-revenue',
      { params }
    )
    return data.data
  }

  async getMarketingSourceStage(params?: {
    startDate?: string
    endDate?: string
    owner_id?: string
  }): Promise<import('@/types').MarketingSourceStageData> {
    const { data } = await this.client.get<ApiResponse<import('@/types').MarketingSourceStageData>>(
      '/reports/marketing/source-stage',
      { params }
    )
    return data.data
  }

  async getMarketingMqlSqlAcceptance(params?: {
    startDate?: string
    endDate?: string
    owner_id?: string
  }): Promise<import('@/types').MarketingMqlSqlAcceptanceData> {
    const { data } = await this.client.get<
      ApiResponse<import('@/types').MarketingMqlSqlAcceptanceData>
    >('/reports/marketing/mql-sql-acceptance', { params })
    return data.data
  }

  // Search
  async globalSearch(
    query: string,
    options?: { limit?: number; offset?: number; entityTypes?: string[] }
  ): Promise<unknown> {
    const { data } = await this.client.get('/search/global', {
      params: {
        q: query,
        limit: options?.limit || 10,
        offset: options?.offset || 0,
        entities: options?.entityTypes?.join(','),
      },
    })
    return data
  }

  async getSearchSuggestions(query: string, limit: number = 8): Promise<unknown> {
    const { data } = await this.client.get('/search/suggestions', {
      params: { q: query, limit },
    })
    return data
  }

  // Custom Reports
  async createCustomReport(reportData: CreateReportData): Promise<CustomReport> {
    const { data } = await this.client.post<ApiResponse<CustomReport>>(
      '/custom-reports',
      reportData
    )
    return data.data
  }

  async getCustomReports(): Promise<SavedReportMetadata[]> {
    const { data } = await this.client.get<ApiResponse<SavedReportMetadata[]>>('/custom-reports')
    return data.data
  }

  async getCustomReport(id: string): Promise<CustomReport> {
    const { data } = await this.client.get<ApiResponse<CustomReport>>(`/custom-reports/${id}`)
    return data.data
  }

  async updateCustomReport(id: string, reportData: UpdateReportData): Promise<CustomReport> {
    const { data } = await this.client.put<ApiResponse<CustomReport>>(
      `/custom-reports/${id}`,
      reportData
    )
    return data.data
  }

  async deleteCustomReport(id: string): Promise<void> {
    await this.client.delete(`/custom-reports/${id}`)
  }

  async executeCustomReport(id: string): Promise<ReportExecutionResult> {
    const { data } = await this.client.post<ApiResponse<ReportExecutionResult>>(
      `/custom-reports/${id}/execute`
    )
    return data.data
  }

  async executeAdHocReport(reportData: ExecuteReportData): Promise<ReportExecutionResult> {
    const { data } = await this.client.post<ApiResponse<ReportExecutionResult>>(
      '/custom-reports/execute',
      reportData
    )
    return data.data
  }

  async getReportFields(
    dataSource: string
  ): Promise<{ name: string; label: string; type: string }[]> {
    const { data } = await this.client.get<
      ApiResponse<{ name: string; label: string; type: string }[]>
    >(`/custom-reports/fields/${dataSource}`)
    return data.data
  }

  async getPublicReports(): Promise<any[]> {
    const { data } = await this.client.get<ApiResponse<any[]>>('/custom-reports/public')
    return data.data
  }

  async shareReport(reportId: string, isPublic: boolean): Promise<any> {
    const { data } = await this.client.post<ApiResponse<any>>(`/custom-reports/${reportId}/share`, {
      isPublic,
    })
    return data.data
  }

  async cloneReport(reportId: string, name?: string): Promise<any> {
    const { data } = await this.client.post<ApiResponse<any>>(`/custom-reports/${reportId}/clone`, {
      name,
    })
    return data.data
  }

  async toggleReportFavorite(reportId: string): Promise<any> {
    const { data } = await this.client.post<ApiResponse<any>>(
      `/custom-reports/${reportId}/favorite`
    )
    return data.data
  }

  async exportReportPdf(reportData: {
    dataSource: string
    config: Record<string, any>
    reportName?: string
  }): Promise<Blob> {
    const { data } = await this.client.post('/custom-reports/export/pdf', reportData, {
      responseType: 'blob',
    })
    return data
  }

  async downloadSavedReportPdf(reportId: string): Promise<Blob> {
    const { data } = await this.client.get(`/custom-reports/${reportId}/pdf`, {
      responseType: 'blob',
    })
    return data
  }

  // Google Integration Methods
  async getGoogleAuthUrl(opts?: {
    syncEmails?: boolean
    syncCalendar?: boolean
  }): Promise<AuthUrlResponse> {
    const params: Record<string, string> = {}
    // Only pass each param when explicitly false (to opt OUT). Omitting
    // means "no preference" — backend preserves existing value on reconnect
    // and defaults to true on new integrations.
    if (opts?.syncEmails === false) params.sync_emails = 'false'
    if (opts?.syncCalendar === false) params.sync_calendar = 'false'
    const { data } = await this.client.get<AuthUrlResponse>('/integrations/google/auth/url', {
      params,
      headers: {
        'x-user-id': localStorage.getItem('gracklecrm_current_user_id') || '005000000000001AAA', // Demo user ID
      },
    })
    return data
  }

  async getIntegrationStatus(): Promise<IntegrationStatus> {
    const { data } = await this.client.get<IntegrationStatus>('/integrations/google/status', {
      headers: {
        'x-user-id': localStorage.getItem('gracklecrm_current_user_id') || '005000000000001AAA',
      },
    })
    return data
  }

  async disconnectGoogleIntegration(): Promise<{ message: string; integration_id: string }> {
    const { data } = await this.client.post<{ message: string; integration_id: string }>(
      '/integrations/google/disconnect',
      {},
      {
        headers: {
          'x-user-id': localStorage.getItem('gracklecrm_current_user_id') || '005000000000001AAA',
        },
      }
    )
    return data
  }

  async triggerGoogleSync(syncType: 'full' | 'incremental' = 'full'): Promise<SyncResponse> {
    const { data } = await this.client.post<SyncResponse>(
      '/integrations/google/sync',
      { syncType },
      {
        headers: {
          'x-user-id': localStorage.getItem('gracklecrm_current_user_id') || '005000000000001AAA',
        },
      }
    )
    return data
  }

  async getSyncStatus(syncId: string): Promise<{ sync: SyncLog }> {
    const { data } = await this.client.get<{ sync: SyncLog }>(
      `/integrations/google/sync/${syncId}`,
      {
        headers: {
          'x-user-id': localStorage.getItem('gracklecrm_current_user_id') || '005000000000001AAA',
        },
      }
    )
    return data
  }

  async updateIntegrationSettings(
    settings: IntegrationSettings
  ): Promise<{ message: string; integration: GoogleIntegration }> {
    const { data } = await this.client.put<{ message: string; integration: GoogleIntegration }>(
      '/integrations/google/settings',
      settings,
      {
        headers: {
          'x-user-id': localStorage.getItem('gracklecrm_current_user_id') || '005000000000001AAA',
        },
      }
    )
    return data
  }

  async scheduleMeet(params: {
    title: string
    description?: string
    start_time: string
    end_time: string
    attendee_emails?: string[]
    entity_type?: string
    entity_id?: string
  }): Promise<{
    success: boolean
    event: any
    meet_link: string
    google_event_id: string
    google_event_link: string
  }> {
    const { data } = await this.client.post('/integrations/google/meet', params, {
      headers: {
        'x-user-id': localStorage.getItem('gracklecrm_current_user_id') || '005000000000001AAA',
      },
    })
    return data
  }

  // Mailchimp Integration Methods
  async connectMailchimp(
    apiKey: string
  ): Promise<{ message: string; account_name: string; account_id: string }> {
    const { data } = await this.client.post(
      '/integrations/mailchimp/connect',
      { api_key: apiKey },
      {
        headers: {
          'x-user-id': localStorage.getItem('gracklecrm_current_user_id') || '005000000000001AAA',
        },
      }
    )
    return data
  }

  async getMailchimpStatus(): Promise<{ connected: boolean; integration?: any }> {
    const { data } = await this.client.get('/integrations/mailchimp/status', {
      headers: {
        'x-user-id': localStorage.getItem('gracklecrm_current_user_id') || '005000000000001AAA',
      },
    })
    return data
  }

  async disconnectMailchimp(): Promise<{ message: string }> {
    const { data } = await this.client.post(
      '/integrations/mailchimp/disconnect',
      {},
      {
        headers: {
          'x-user-id': localStorage.getItem('gracklecrm_current_user_id') || '005000000000001AAA',
        },
      }
    )
    return data
  }

  async setMailchimpAudience(audienceId: string): Promise<{ message: string; audienceId: string }> {
    const { data } = await this.client.put(
      '/integrations/mailchimp/audience',
      { audienceId },
      {
        headers: {
          'x-user-id': localStorage.getItem('gracklecrm_current_user_id') || '005000000000001AAA',
        },
      }
    )
    return data
  }

  // Dry-run preview: what a full sync WOULD push, by resulting Mailchimp
  // status. Read-only. When audienceId is given, also returns a diff against
  // that audience's current members (new-vs-update + subscribed downgrades).
  async getMailchimpSyncPreview(audienceId?: string): Promise<MailchimpSyncPreview> {
    const { data } = await this.client.get<MailchimpSyncPreview>(
      '/integrations/mailchimp/sync/preview',
      {
        params: audienceId ? { audienceId } : undefined,
        headers: {
          'x-user-id': localStorage.getItem('gracklecrm_current_user_id') || '005000000000001AAA',
        },
      }
    )
    return data
  }

  async triggerMailchimpSync(
    syncType: string,
    audienceId: string
  ): Promise<{ sync_id: string; sync_type: string; audience_id: string }> {
    const { data } = await this.client.post(
      '/integrations/mailchimp/sync',
      { syncType, audienceId },
      {
        headers: {
          'x-user-id': localStorage.getItem('gracklecrm_current_user_id') || '005000000000001AAA',
        },
      }
    )
    return data
  }

  async getMailchimpSyncStatus(syncId: string): Promise<{ sync: any }> {
    const { data } = await this.client.get(`/integrations/mailchimp/sync/${syncId}`, {
      headers: {
        'x-user-id': localStorage.getItem('gracklecrm_current_user_id') || '005000000000001AAA',
      },
    })
    return data
  }

  // Google Chat Integration (admin-only, app-wide)
  async getGoogleChatStatus(): Promise<{
    configured: boolean
    is_enabled: boolean
    has_webhook_url: boolean
    webhook_url_masked: string
    enabled_events: string[]
    last_modified_date: string | null
    updated_by_name: string | null
  }> {
    const { data } = await this.client.get('/integrations/google-chat/status')
    return data
  }

  async updateGoogleChatSettings(settings: {
    webhook_url?: string
    is_enabled?: boolean
    enabled_events?: string[]
  }): Promise<{ message: string }> {
    const { data } = await this.client.put('/integrations/google-chat/settings', settings)
    return data
  }

  async testGoogleChat(): Promise<{ success: boolean; message: string }> {
    const { data } = await this.client.post('/integrations/google-chat/test')
    return data
  }

  // Notifications
  async getNotifications(
    page = 1,
    limit = 20,
    unread = false
  ): Promise<{ data: any[]; total: number; page: number; limit: number }> {
    const { data } = await this.client.get('/notifications', {
      params: { page, limit, ...(unread ? { unread: true } : {}) },
    })
    return data
  }

  async getUnreadNotificationCount(): Promise<{ count: number }> {
    const { data } = await this.client.get('/notifications/unread-count')
    return data
  }

  async markNotificationRead(id: string): Promise<{ success: boolean }> {
    const { data } = await this.client.patch(`/notifications/${id}/read`)
    return data
  }

  async markAllNotificationsRead(): Promise<{ success: boolean }> {
    const { data } = await this.client.post('/notifications/mark-all-read')
    return data
  }

  // Enhancement Requests
  async getEnhancementRequests(
    params?: PaginationParams & {
      status?: string
      priority?: string
      category?: string
      assigned_to?: string
      requested_by?: string
    }
  ): Promise<ApiResponse<any[]>> {
    const { data } = await this.client.get<ApiResponse<any[]>>('/enhancement-requests', { params })
    return data
  }

  async getEnhancementRequest(id: string): Promise<any> {
    const { data } = await this.client.get<ApiResponse<any>>(`/enhancement-requests/${id}`)
    return data.data
  }

  async getEnhancementRequestStats(): Promise<any> {
    const { data } = await this.client.get<any>('/enhancement-requests/stats/summary')
    return data
  }

  async createEnhancementRequest(enhancementRequest: any): Promise<any> {
    const { data } = await this.client.post<any>('/enhancement-requests', enhancementRequest)
    return data
  }

  async updateEnhancementRequest(id: string, enhancementRequest: any): Promise<any> {
    const { data } = await this.client.patch<any>(`/enhancement-requests/${id}`, enhancementRequest)
    return data
  }

  async deleteEnhancementRequest(id: string): Promise<void> {
    await this.client.delete(`/enhancement-requests/${id}`)
  }

  // Tickets (Support)
  async getTickets(
    params?: PaginationParams & {
      status?: string
      priority?: string
      account_id?: string
      lead_id?: string
    }
  ): Promise<ApiResponse<Ticket[]>> {
    const { data } = await this.client.get<ApiResponse<Ticket[]>>('/tickets', { params })
    return data
  }

  async getTicketStats(): Promise<TicketStats> {
    const { data } = await this.client.get<{ data: TicketStats }>('/tickets/stats')
    return data.data
  }

  async getTicket(id: string): Promise<Ticket> {
    const { data } = await this.client.get<ApiResponse<Ticket>>(`/tickets/${id}`)
    return data.data
  }

  async createTicket(ticket: Partial<Ticket>): Promise<Ticket> {
    const { data } = await this.client.post<ApiResponse<Ticket>>('/tickets', ticket)
    return data.data
  }

  async updateTicket(id: string, ticket: Partial<Ticket>): Promise<Ticket> {
    const { data } = await this.client.patch<ApiResponse<Ticket>>(`/tickets/${id}`, ticket)
    return data.data
  }

  async deleteTicket(id: string): Promise<void> {
    await this.client.delete(`/tickets/${id}`)
  }

  async addTicketComment(
    ticketId: string,
    comment: Partial<TicketComment>
  ): Promise<TicketComment> {
    const { data } = await this.client.post<ApiResponse<TicketComment>>(
      `/tickets/${ticketId}/comments`,
      comment
    )
    return data.data
  }

  async updateTicketComment(
    ticketId: string,
    commentId: string,
    updates: { body?: string; is_public?: boolean }
  ): Promise<TicketComment> {
    const { data } = await this.client.patch<ApiResponse<TicketComment>>(
      `/tickets/${ticketId}/comments/${commentId}`,
      updates
    )
    return data.data
  }

  async deleteTicketComment(ticketId: string, commentId: string): Promise<{ success: boolean }> {
    const { data } = await this.client.delete(`/tickets/${ticketId}/comments/${commentId}`)
    return data
  }

  async bulkDeleteTickets(ids: string[]): Promise<{ success: boolean; count: number }> {
    const { data } = await this.client.post('/tickets/bulk-delete', { ids })
    return data
  }

  async bulkUpdateTickets(
    ids: string[],
    updates: Record<string, any>
  ): Promise<{ success: boolean; count: number; updates: Record<string, any> }> {
    const { data } = await this.client.post('/tickets/bulk-update', { ids, updates })
    return data
  }

  // Ticket Emails
  async getTicketEmails(ticketId: string): Promise<Email[]> {
    const { data } = await this.client.get<ApiResponse<Email[]>>(`/tickets/${ticketId}/emails`)
    return data.data
  }

  async getTicketTasks(ticketId: string): Promise<Task[]> {
    const { data } = await this.client.get<ApiResponse<Task[]>>(`/tickets/${ticketId}/tasks`)
    return data.data
  }

  async createTicketTask(ticketId: string, task: Partial<Task>): Promise<Task> {
    const { data } = await this.client.post<ApiResponse<Task>>(`/tickets/${ticketId}/tasks`, task)
    return data.data
  }

  async linkEmailToTicket(ticketId: string, emailId: string): Promise<Email> {
    const { data } = await this.client.post<ApiResponse<Email>>(`/tickets/${ticketId}/emails`, {
      email_id: emailId,
    })
    return data.data
  }

  async sendTicketEmail(
    ticketId: string,
    email: { to: string; subject: string; body: string; html_body?: string }
  ): Promise<{ success: boolean; emailId: string; email: Email }> {
    const { data } = await this.client.post(`/tickets/${ticketId}/emails/send`, email)
    return data.data
  }

  async unlinkEmailFromTicket(ticketId: string, emailId: string): Promise<Email> {
    const { data } = await this.client.delete<ApiResponse<Email>>(
      `/tickets/${ticketId}/emails/${emailId}`
    )
    return data.data
  }

  // Scheduled Reports
  async sendDailyReport(): Promise<{
    success: boolean
    runId?: string
    recipients?: number
    duration?: string
    generatedAt?: string
    reason?: string
    error?: string
  }> {
    const { data } = await this.client.post('/scheduled/daily-report')
    return data
  }

  getDailyReportPreviewUrl(): string {
    return '/api/v1/scheduled/daily-report/preview?format=html'
  }

  // Authentication — delegates to the shared single-flight refresh so manual
  // (session-warning) and automatic (401 interceptor) refreshes don't collide.
  // Callers only care whether they still have a usable session, so a transient
  // failure (429/5xx/network) reports success: the token we hold may still be
  // valid, and the next request will retry the refresh if it isn't.
  async refreshAccessToken(): Promise<boolean> {
    return (await refreshSession()) !== 'dead'
  }

  // ============================================
  // Duplicate Detection
  // ============================================

  async checkDuplicates(
    entityType: string,
    data: Record<string, unknown>
  ): Promise<DuplicateCheckResult> {
    const { data: resp } = await this.client.post<ApiResponse<DuplicateCheckResult>>(
      '/duplicates/check',
      { entityType, data }
    )
    return resp.data
  }

  async getDuplicates(
    entityType: string,
    params?: PaginationParams & {
      status?: string
      confidence_level?: string
    }
  ): Promise<ApiResponse<DuplicateMatch[]>> {
    const { data } = await this.client.get<ApiResponse<DuplicateMatch[]>>(
      `/duplicates/${entityType}`,
      { params }
    )
    return data
  }

  async scanDuplicates(
    entityType: string
  ): Promise<{ batchId: string; scannedCount: number; matchesFound: number }> {
    const { data } = await this.client.post<
      ApiResponse<{ batchId: string; scannedCount: number; matchesFound: number }>
    >(`/duplicates/scan/${entityType}`)
    return data.data
  }

  async resolveDuplicate(id: string, resolution: string): Promise<DuplicateMatch> {
    const { data } = await this.client.post<ApiResponse<DuplicateMatch>>(
      `/duplicates/${id}/resolve`,
      { resolution }
    )
    return data.data
  }

  async getDuplicateStats(): Promise<DuplicateStats> {
    const { data } = await this.client.get<ApiResponse<DuplicateStats>>('/duplicates/stats')
    return data.data
  }

  // ============================================
  // Merge
  // ============================================

  async getMergePreview(
    entityType: string,
    masterId: string,
    loserId: string
  ): Promise<MergePreview> {
    const { data } = await this.client.get<ApiResponse<MergePreview>>(
      `/merge/${entityType}/preview`,
      { params: { masterId, loserId } }
    )
    return data.data
  }

  async mergeRecords(
    entityType: string,
    masterId: string,
    loserId: string,
    fieldSelections: FieldSelection
  ): Promise<Record<string, unknown>> {
    const { data } = await this.client.post<ApiResponse<Record<string, unknown>>>(
      `/merge/${entityType}`,
      { masterId, loserId, fieldSelections }
    )
    return data.data
  }

  // ========== Picklist Management ==========

  async getPicklists(includeInactive = false): Promise<PicklistData> {
    const { data } = await this.client.get<{ data: PicklistData }>('/picklists', {
      params: includeInactive ? { include_inactive: 'true' } : undefined,
    })
    return data.data
  }

  async getPicklistValues(
    entity: string,
    field: string,
    includeInactive = false
  ): Promise<PicklistValue[]> {
    const { data } = await this.client.get<{ data: PicklistValue[] }>(
      `/picklists/${entity}/${field}`,
      {
        params: includeInactive ? { include_inactive: 'true' } : undefined,
      }
    )
    return data.data
  }

  async addPicklistValue(
    entity: string,
    field: string,
    value: Partial<PicklistValue>
  ): Promise<PicklistValue> {
    const { data } = await this.client.post<{ data: PicklistValue }>(
      `/picklists/${entity}/${field}`,
      value
    )
    return data.data
  }

  async updatePicklistValue(id: number, updates: Partial<PicklistValue>): Promise<PicklistValue> {
    const { data } = await this.client.put<{ data: PicklistValue }>(`/picklists/${id}`, updates)
    return data.data
  }

  async reorderPicklistValues(
    entity: string,
    field: string,
    order: { id: number; sort_order: number }[]
  ): Promise<void> {
    await this.client.put(`/picklists/${entity}/${field}/reorder`, { order })
  }

  async deactivatePicklistValue(id: number): Promise<void> {
    await this.client.delete(`/picklists/${id}`)
  }

  async reactivatePicklistValue(id: number): Promise<PicklistValue> {
    const { data } = await this.client.put<{ data: PicklistValue }>(`/picklists/${id}`, {
      is_active: true,
    })
    return data.data
  }

  // ========== Renewal Task Templates ==========

  async getRenewalTemplates(): Promise<RenewalTaskTemplate[]> {
    const { data } = await this.client.get<{ data: RenewalTaskTemplate[] }>('/renewal-templates')
    return data.data
  }

  async createRenewalTemplate(
    template: Partial<RenewalTaskTemplate>
  ): Promise<RenewalTaskTemplate> {
    const { data } = await this.client.post<{ data: RenewalTaskTemplate }>(
      '/renewal-templates',
      template
    )
    return data.data
  }

  async updateRenewalTemplate(
    id: number,
    updates: Partial<RenewalTaskTemplate>
  ): Promise<RenewalTaskTemplate> {
    const { data } = await this.client.put<{ data: RenewalTaskTemplate }>(
      `/renewal-templates/${id}`,
      updates
    )
    return data.data
  }

  async deleteRenewalTemplate(id: number): Promise<void> {
    await this.client.delete(`/renewal-templates/${id}`)
  }

  async reorderRenewalTemplates(order: { id: number; sort_order: number }[]): Promise<void> {
    await this.client.put('/renewal-templates/reorder', { order })
  }

  async reorderQuoteLineItems(
    quoteId: string,
    order: { id: string; sort_order: number }[]
  ): Promise<void> {
    await this.client.put(`/quotes/${quoteId}/line-items/reorder`, { order })
  }

  async logout(): Promise<void> {
    try {
      // Attempt to call logout endpoint
      await this.client.post('/auth/logout')
    } catch (error) {
      console.error('Logout API error:', error)
      // Continue with cleanup even if API call fails
    } finally {
      // Always clear local storage
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      localStorage.removeItem('gracklecrm_current_user_id')
      localStorage.removeItem('gracklecrm_user_type')

      // Redirect to login
      window.location.href = '/login'
    }
  }

  // RFPs
  async getRfps(params?: PaginationParams): Promise<ApiResponse<import('@/types').Rfp[]>> {
    const { data } = await this.client.get<ApiResponse<import('@/types').Rfp[]>>('/rfps', {
      params,
    })
    return data
  }

  async getRfp(id: string): Promise<import('@/types').Rfp> {
    const { data } = await this.client.get<ApiResponse<import('@/types').Rfp>>(`/rfps/${id}`)
    return data.data
  }

  async getRfpPipeline(): Promise<import('@/types').RfpPipelineStage[]> {
    const { data } =
      await this.client.get<ApiResponse<import('@/types').RfpPipelineStage[]>>('/rfps/pipeline')
    return data.data
  }

  async getRfpStats(): Promise<any> {
    const { data } = await this.client.get<ApiResponse<any>>('/rfps/stats')
    return data.data
  }

  async getRfpTags(): Promise<import('@/types').RfpTag[]> {
    const { data } = await this.client.get<ApiResponse<import('@/types').RfpTag[]>>('/rfps/tags')
    return data.data
  }

  async createRfp(rfp: Partial<import('@/types').Rfp>): Promise<import('@/types').Rfp> {
    const { data } = await this.client.post<ApiResponse<import('@/types').Rfp>>('/rfps', rfp)
    return data.data
  }

  async updateRfp(id: string, rfp: Partial<import('@/types').Rfp>): Promise<import('@/types').Rfp> {
    const { data } = await this.client.put<ApiResponse<import('@/types').Rfp>>(`/rfps/${id}`, rfp)
    return data.data
  }

  async updateRfpStage(id: string, stage: string): Promise<import('@/types').Rfp> {
    const { data } = await this.client.put<ApiResponse<import('@/types').Rfp>>(
      `/rfps/${id}/stage`,
      { stage }
    )
    return data.data
  }

  async deleteRfp(id: string): Promise<void> {
    await this.client.delete(`/rfps/${id}`)
  }

  async createRfpTask(
    rfpId: string,
    task: Partial<import('@/types').RfpTask> & { notify_assignee?: boolean }
  ): Promise<import('@/types').RfpTask> {
    const { data } = await this.client.post<ApiResponse<import('@/types').RfpTask>>(
      `/rfps/${rfpId}/tasks`,
      task
    )
    return data.data
  }

  async updateRfpTask(
    rfpId: string,
    taskId: string,
    task: Partial<import('@/types').RfpTask> & { notify_assignee?: boolean }
  ): Promise<import('@/types').RfpTask> {
    const { data } = await this.client.put<ApiResponse<import('@/types').RfpTask>>(
      `/rfps/${rfpId}/tasks/${taskId}`,
      task
    )
    return data.data
  }

  async deleteRfpTask(rfpId: string, taskId: string): Promise<void> {
    await this.client.delete(`/rfps/${rfpId}/tasks/${taskId}`)
  }

  async addRfpTags(rfpId: string, tagIds: string[]): Promise<any> {
    const { data } = await this.client.post(`/rfps/${rfpId}/tags`, { tag_ids: tagIds })
    return data.data
  }

  async removeRfpTag(rfpId: string, tagId: string): Promise<void> {
    await this.client.delete(`/rfps/${rfpId}/tags/${tagId}`)
  }

  async addRfpDocument(
    rfpId: string,
    doc: Partial<import('@/types').RfpDocument>
  ): Promise<import('@/types').RfpDocument> {
    const { data } = await this.client.post<ApiResponse<import('@/types').RfpDocument>>(
      `/rfps/${rfpId}/documents`,
      doc
    )
    return data.data
  }

  async deleteRfpDocument(rfpId: string, docId: string): Promise<void> {
    await this.client.delete(`/rfps/${rfpId}/documents/${docId}`)
  }

  async addRfpContact(rfpId: string, contactId: string, role?: string): Promise<any> {
    const { data } = await this.client.post(`/rfps/${rfpId}/contacts`, {
      contact_id: contactId,
      role,
    })
    return data.data
  }

  async removeRfpContact(rfpId: string, contactId: string): Promise<void> {
    await this.client.delete(`/rfps/${rfpId}/contacts/${contactId}`)
  }

  // ---------- Mass upload ----------

  async massUploadPreview(file: File): Promise<import('@/types').MassUploadPreview> {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await this.client.post<{ data: import('@/types').MassUploadPreview }>(
      '/opportunities/mass-upload/preview',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return data.data
  }

  async massUploadCommit(payload: {
    preview_token: string
    acknowledge_no_attachments: boolean
    skip_warnings?: boolean
  }): Promise<import('@/types').MassUploadResult> {
    const { data } = await this.client.post<{ data: import('@/types').MassUploadResult }>(
      '/opportunities/mass-upload/commit',
      payload
    )
    return data.data
  }

  async listImportBatches(): Promise<import('@/types').ImportBatch[]> {
    const { data } = await this.client.get<{ data: import('@/types').ImportBatch[] }>(
      '/opportunities/mass-upload/batches'
    )
    return data.data
  }

  async getImportBatch(id: string): Promise<import('@/types').ImportBatchDetail> {
    const { data } = await this.client.get<{ data: import('@/types').ImportBatchDetail }>(
      `/opportunities/mass-upload/batches/${id}`
    )
    return data.data
  }

  async rollbackImportBatch(id: string): Promise<{ batch_id: string; rolled_back_count: number }> {
    const { data } = await this.client.post<{
      data: { batch_id: string; rolled_back_count: number }
    }>(`/opportunities/mass-upload/batches/${id}/rollback`)
    return data.data
  }

  async normalizeMassUploadCurrencies(): Promise<import('@/types').CurrencyNormalizationReport> {
    const { data } = await this.client.post<{
      data: import('@/types').CurrencyNormalizationReport
    }>('/opportunities/mass-upload/normalize-currency')
    return data.data
  }

  async applyMassUploadProductMappings(
    token: string,
    overrides: Record<string, string | null>
  ): Promise<import('@/types').MassUploadPreview> {
    const { data } = await this.client.patch<{ data: import('@/types').MassUploadPreview }>(
      `/opportunities/mass-upload/preview/${token}/product-mappings`,
      { overrides }
    )
    return data.data
  }

  // Note Session methods
  async getNoteSessions(params?: {
    q?: string
    status?: string
    who_id?: string
    what_id?: string
    page?: number
    limit?: number
  }): Promise<{
    data: import('@/types/noteSession').NoteSession[]
    total: number
    page: number
    limit: number
  }> {
    const { data } = await this.client.get('/note-sessions', { params })
    return data
  }

  async getNoteSession(id: string): Promise<import('@/types/noteSession').NoteSession> {
    const { data } = await this.client.get(`/note-sessions/${id}`)
    return data.data
  }

  async getNoteSessionsByContact(
    contactId: string
  ): Promise<import('@/types/noteSession').NoteSession[]> {
    const { data } = await this.client.get(`/note-sessions/by-contact/${contactId}`)
    return data.data
  }

  async getNoteSessionsByLead(
    leadId: string
  ): Promise<import('@/types/noteSession').NoteSession[]> {
    const { data } = await this.client.get(`/note-sessions/by-lead/${leadId}`)
    return data.data
  }

  async getNoteSessionsByOpportunity(
    opportunityId: string
  ): Promise<import('@/types/noteSession').NoteSession[]> {
    const { data } = await this.client.get(`/note-sessions/by-opportunity/${opportunityId}`)
    return data.data
  }

  async getNoteSessionsByAccount(
    accountId: string
  ): Promise<import('@/types/noteSession').NoteSession[]> {
    const { data } = await this.client.get(`/note-sessions/by-account/${accountId}`)
    return data.data
  }

  async createNoteSession(
    input: import('@/types/noteSession').CreateNoteSessionInput
  ): Promise<import('@/types/noteSession').NoteSession> {
    const { data } = await this.client.post('/note-sessions', input)
    return data.data
  }

  async updateNoteSession(
    id: string,
    input: import('@/types/noteSession').UpdateNoteSessionInput
  ): Promise<import('@/types/noteSession').NoteSession> {
    const { data } = await this.client.put(`/note-sessions/${id}`, input)
    return data.data
  }

  async autosaveNoteSession(
    id: string,
    input: import('@/types/noteSession').AutosaveNoteSessionInput
  ): Promise<void> {
    await this.client.patch(`/note-sessions/${id}/autosave`, input)
  }

  async completeNoteSession(
    id: string,
    body?: { notes_content?: string; notes_plain_text?: string }
  ): Promise<import('@/types/noteSession').NoteSession> {
    const { data } = await this.client.post(`/note-sessions/${id}/complete`, body ?? {})
    return data.data
  }

  async deleteNoteSession(id: string): Promise<void> {
    await this.client.delete(`/note-sessions/${id}`)
  }

  // Note Template methods
  async getNoteTemplates(): Promise<import('@/types/noteSession').NoteTemplate[]> {
    const { data } = await this.client.get('/note-templates')
    return data.data
  }

  async getNoteTemplate(id: string): Promise<import('@/types/noteSession').NoteTemplate> {
    const { data } = await this.client.get(`/note-templates/${id}`)
    return data.data
  }

  async createNoteTemplate(
    input: import('@/types/noteSession').CreateNoteTemplateInput
  ): Promise<import('@/types/noteSession').NoteTemplate> {
    const { data } = await this.client.post('/note-templates', input)
    return data.data
  }

  async updateNoteTemplate(
    id: string,
    input: import('@/types/noteSession').UpdateNoteTemplateInput
  ): Promise<import('@/types/noteSession').NoteTemplate> {
    const { data } = await this.client.put(`/note-templates/${id}`, input)
    return data.data
  }

  // ========== HR Module — Phase 1 ==========

  async getEmployees(
    params?: import('@/types/hr').EmployeeListParams
  ): Promise<import('@/types/hr').EmployeeListResponse> {
    const response = await this.client.get<import('@/types/hr').EmployeeListResponse>(
      '/hr/employees',
      { params }
    )
    return response.data
  }

  async getEmployee(
    id: string
  ): Promise<{ data: import('@/types/hr').Employee; restricted?: boolean }> {
    const response = await this.client.get<{
      data: import('@/types/hr').Employee
      restricted?: boolean
    }>(`/hr/employees/${id}`)
    return response.data
  }

  async getMyEmployeeRecord(): Promise<import('@/types/hr').Employee> {
    const response = await this.client.get<{ data: import('@/types/hr').Employee }>(
      '/hr/employees/me'
    )
    return response.data.data
  }

  async createEmployee(
    data: Partial<import('@/types/hr').Employee>
  ): Promise<import('@/types/hr').Employee> {
    const response = await this.client.post<{ data: import('@/types/hr').Employee }>(
      '/hr/employees',
      data
    )
    return response.data.data
  }

  async updateEmployee(
    id: string,
    data: Partial<import('@/types/hr').Employee>
  ): Promise<import('@/types/hr').Employee> {
    const response = await this.client.put<{ data: import('@/types/hr').Employee }>(
      `/hr/employees/${id}`,
      data
    )
    return response.data.data
  }

  async deleteEmployee(id: string): Promise<void> {
    await this.client.delete(`/hr/employees/${id}`)
  }

  async getUpcomingBirthdays(): Promise<import('@/types/hr').BirthdayEntry[]> {
    const response = await this.client.get<{ data: import('@/types/hr').BirthdayEntry[] }>(
      '/hr/employees/birthdays'
    )
    return response.data.data
  }

  async getUpcomingAnniversaries(): Promise<import('@/types/hr').AnniversaryEntry[]> {
    const response = await this.client.get<{ data: import('@/types/hr').AnniversaryEntry[] }>(
      '/hr/employees/anniversaries'
    )
    return response.data.data
  }

  async getDepartments(includeInactive = false): Promise<import('@/types/hr').Department[]> {
    const response = await this.client.get<{ data: import('@/types/hr').Department[] }>(
      '/hr/departments',
      {
        params: { include_inactive: includeInactive ? 'true' : undefined },
      }
    )
    return response.data.data
  }

  async getDepartment(id: string): Promise<import('@/types/hr').Department> {
    const response = await this.client.get<{ data: import('@/types/hr').Department }>(
      `/hr/departments/${id}`
    )
    return response.data.data
  }

  async createDepartment(
    data: Partial<import('@/types/hr').Department>
  ): Promise<import('@/types/hr').Department> {
    const response = await this.client.post<{ data: import('@/types/hr').Department }>(
      '/hr/departments',
      data
    )
    return response.data.data
  }

  async updateDepartment(
    id: string,
    data: Partial<import('@/types/hr').Department>
  ): Promise<import('@/types/hr').Department> {
    const response = await this.client.put<{ data: import('@/types/hr').Department }>(
      `/hr/departments/${id}`,
      data
    )
    return response.data.data
  }

  async deleteDepartment(id: string): Promise<void> {
    await this.client.delete(`/hr/departments/${id}`)
  }

  async getEmergencyContacts(employeeId: string): Promise<import('@/types/hr').EmergencyContact[]> {
    const response = await this.client.get<{ data: import('@/types/hr').EmergencyContact[] }>(
      `/hr/employees/${employeeId}/emergency-contacts`
    )
    return response.data.data
  }

  async createEmergencyContact(
    employeeId: string,
    data: Partial<import('@/types/hr').EmergencyContact>
  ): Promise<import('@/types/hr').EmergencyContact> {
    const response = await this.client.post<{ data: import('@/types/hr').EmergencyContact }>(
      `/hr/employees/${employeeId}/emergency-contacts`,
      data
    )
    return response.data.data
  }

  async updateEmergencyContact(
    employeeId: string,
    id: string,
    data: Partial<import('@/types/hr').EmergencyContact>
  ): Promise<import('@/types/hr').EmergencyContact> {
    const response = await this.client.put<{ data: import('@/types/hr').EmergencyContact }>(
      `/hr/employees/${employeeId}/emergency-contacts/${id}`,
      data
    )
    return response.data.data
  }

  async deleteEmergencyContact(employeeId: string, id: string): Promise<void> {
    await this.client.delete(`/hr/employees/${employeeId}/emergency-contacts/${id}`)
  }

  async getOrgChart(): Promise<{
    data: import('@/types/hr').OrgChartNode[]
    count: number
    dottedLines?: import('@/types/hr').DottedLineEdge[]
  }> {
    const response = await this.client.get<{
      data: import('@/types/hr').OrgChartNode[]
      count: number
      dottedLines?: import('@/types/hr').DottedLineEdge[]
    }>('/hr/org-chart')
    return response.data
  }

  async importOrgChartCsv(csv: string): Promise<import('@/types/hr').OrgChartImportSummary> {
    const response = await this.client.post<{
      data: import('@/types/hr').OrgChartImportSummary
    }>('/hr/employees/import-csv', { csv })
    return response.data.data
  }

  async importBrightHr(csv: string): Promise<import('@/types/hr').BrightHrImportSummary> {
    const response = await this.client.post<{
      data: import('@/types/hr').BrightHrImportSummary
    }>('/hr/employees/import-brighthr', { csv })
    return response.data.data
  }

  async getEmployeeSensitive(id: string): Promise<import('@/types/hr').EmployeeSensitive> {
    const response = await this.client.get<{ data: import('@/types/hr').EmployeeSensitive }>(
      `/hr/employees/${id}/sensitive`
    )
    return response.data.data
  }

  async updateEmployeeSensitive(
    id: string,
    data: Partial<import('@/types/hr').EmployeeSensitive>
  ): Promise<void> {
    await this.client.put(`/hr/employees/${id}/sensitive`, data)
  }

  async getEmployeeDocuments(id: string): Promise<import('@/types/hr').EmployeeDocument[]> {
    const r = await this.client.get<{ data: import('@/types/hr').EmployeeDocument[] }>(
      `/hr/employees/${id}/documents`
    )
    return r.data.data
  }

  async uploadEmployeeDocument(
    id: string,
    formData: FormData
  ): Promise<import('@/types/hr').EmployeeDocument> {
    const r = await this.client.post<{ data: import('@/types/hr').EmployeeDocument }>(
      `/hr/employees/${id}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return r.data.data
  }

  async downloadEmployeeDocument(id: string, docId: string): Promise<Blob> {
    const r = await this.client.get(`/hr/employees/${id}/documents/${docId}/download`, {
      responseType: 'blob',
    })
    return r.data as Blob
  }

  async deleteEmployeeDocument(id: string, docId: string): Promise<void> {
    await this.client.delete(`/hr/employees/${id}/documents/${docId}`)
  }

  // ----- Company documents (HR library, Admin + Super Admin) ----------------
  async getCompanyDocuments(): Promise<import('@/types/hr').CompanyDocument[]> {
    const r = await this.client.get<{ data: import('@/types/hr').CompanyDocument[] }>(
      '/hr/company-documents'
    )
    return r.data.data
  }

  async uploadCompanyDocument(formData: FormData): Promise<import('@/types/hr').CompanyDocument> {
    const r = await this.client.post<{ data: import('@/types/hr').CompanyDocument }>(
      '/hr/company-documents',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return r.data.data
  }

  async downloadCompanyDocument(docId: string): Promise<Blob> {
    const r = await this.client.get(`/hr/company-documents/${docId}/download`, {
      responseType: 'blob',
    })
    return r.data as Blob
  }

  async deleteCompanyDocument(docId: string): Promise<void> {
    await this.client.delete(`/hr/company-documents/${docId}`)
  }

  /** Mint a short-lived, token-gated URL to download the complete HR export ZIP. */
  async getHrExportLink(): Promise<{ url: string }> {
    const r = await this.client.get<{ url: string }>('/hr/export/link')
    return r.data
  }

  /** The requester's HR role flags (employeeId, isHrAdmin, isManager, …). */
  async getHrContext(): Promise<import('@/hooks/use-api').HrContext> {
    const r = await this.client.get<{ data: import('@/hooks/use-api').HrContext }>(
      '/hr/employees/me/context'
    )
    return r.data.data
  }

  // ----- Time off / leave ----------------------------------------------------
  async getTimeOffTypes(): Promise<import('@/types/hr').TimeOffType[]> {
    const r = await this.client.get<{ data: import('@/types/hr').TimeOffType[] }>(
      '/hr/time-off/types'
    )
    return r.data.data
  }

  async getTimeOffBalances(params?: {
    employee_id?: string
    year?: number
  }): Promise<import('@/types/hr').TimeOffBalance[]> {
    const r = await this.client.get<{ data: import('@/types/hr').TimeOffBalance[] }>(
      '/hr/time-off/balances',
      { params }
    )
    return r.data.data
  }

  async getTimeOffRequests(params?: {
    status?: string
    scope?: string
  }): Promise<import('@/types/hr').TimeOffRequest[]> {
    const r = await this.client.get<{ data: import('@/types/hr').TimeOffRequest[] }>(
      '/hr/time-off/requests',
      { params }
    )
    return r.data.data
  }

  async createTimeOffRequest(data: {
    type_id: string
    start_date: string
    end_date: string
    amount: number
    unit?: string
    reason?: string
    employee_id?: string
  }): Promise<import('@/types/hr').TimeOffRequest> {
    const r = await this.client.post<{ data: import('@/types/hr').TimeOffRequest }>(
      '/hr/time-off/requests',
      data
    )
    return r.data.data
  }

  async decideTimeOffRequest(
    id: string,
    decision: 'approve' | 'reject',
    note?: string
  ): Promise<{ status: string; warnings?: string[] }> {
    const r = await this.client.post<{ data: { status: string }; warnings?: string[] }>(
      `/hr/time-off/requests/${id}/decision`,
      { decision, note }
    )
    return { status: r.data.data.status, warnings: r.data.warnings }
  }

  async cancelTimeOffRequest(id: string): Promise<void> {
    await this.client.post(`/hr/time-off/requests/${id}/cancel`, {})
  }

  async setTimeOffEntitlement(data: {
    employee_id: string
    type_id: string
    year?: number
    allowance: number
    carried_over?: number
  }): Promise<void> {
    await this.client.post('/hr/time-off/entitlements', data)
  }

  async deleteNoteTemplate(id: string): Promise<void> {
    await this.client.delete(`/note-templates/${id}`)
  }

  // --- Contract reviews --------------------------------------------------------
  //
  // Note the path: /contract-reviews. Distinct from the account's contracts_folder_url, which
  // is a link to where a customer's paperwork lives. Related in the domain, different features.

  async uploadContractReviewDocument(
    reviewId: string,
    file: File,
    kind: 'draft' | 'signature'
  ): Promise<ContractReviewDocument> {
    const form = new FormData()
    form.append('file', file)
    form.append('document_kind', kind)
    const { data } = await this.client.post<ContractReviewDocument>(
      `/contract-reviews/${reviewId}/documents`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
    )
    return data
  }

  async getContractReviewDocuments(reviewId: string): Promise<ContractReviewDocument[]> {
    const { data } = await this.client.get<{ data: ContractReviewDocument[] }>(
      `/contract-reviews/${reviewId}/documents`
    )
    return data.data
  }

  /**
   * Streamed through the backend, which checks the caller is a participant first. There is no
   * public URL for these files and deliberately so: a signed URL would be forwardable to anyone.
   */
  async downloadContractReviewDocument(
    reviewId: string,
    documentId: string
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.client.get(
      `/contract-reviews/${reviewId}/documents/${documentId}/download`,
      { responseType: 'blob' }
    )
    const disposition = String(response.headers['content-disposition'] ?? '')
    const match = disposition.match(/filename="([^"]+)"/)
    return {
      blob: response.data as Blob,
      filename: match?.[1] ? decodeURIComponent(match[1]) : 'contract-document',
    }
  }

  async deleteContractReviewDocument(reviewId: string, documentId: string): Promise<void> {
    await this.client.delete(`/contract-reviews/${reviewId}/documents/${documentId}`)
  }

  async getContractReviews(
    params: {
      page?: number
      limit?: number
      status?: string
      account_id?: string
      include_closed?: boolean
      /** Narrow to reviews you raised or are reviewing. The list is team-wide without it. */
      mine?: boolean
    } = {}
  ): Promise<ContractReviewsPage> {
    const { data } = await this.client.get<ContractReviewsPage>('/contract-reviews', { params })
    return data
  }

  /**
   * The once-a-day welcome-back summary. "Today" is sent from the browser because the server runs
   * in UTC: the date for due tasks, and the instants bounding today for meetings.
   */
  async getWelcomeSummary(window: {
    client_date: string
    day_start: string
    day_end: string
  }): Promise<WelcomeSummary> {
    const { data } = await this.client.get<{ data: WelcomeSummary }>('/welcome/summary', {
      params: window,
    })
    return data.data
  }

  async getContractReview(id: string): Promise<ContractReview> {
    const { data } = await this.client.get<ContractReview>(`/contract-reviews/${id}`)
    return data
  }

  async createContractReview(payload: {
    title: string
    account_id: string
    reviewer_id: string
    opportunity_id?: string | null
    draft_document_url?: string | null
  }): Promise<ContractReview> {
    const { data } = await this.client.post<ContractReview>('/contract-reviews', payload)
    return data
  }

  async updateContractReview(
    id: string,
    payload: Partial<{
      title: string
      opportunity_id: string | null
      draft_document_url: string | null
      signature_document_url: string | null
      reviewer_id: string
    }>
  ): Promise<ContractReview> {
    const { data } = await this.client.patch<ContractReview>(`/contract-reviews/${id}`, payload)
    return data
  }

  /** Move the review along. A comment can ride along, because a redline is both at once. */
  async setContractReviewStatus(
    id: string,
    status: ContractReviewStatus,
    comment?: string
  ): Promise<ContractReview> {
    const { data } = await this.client.post<ContractReview>(`/contract-reviews/${id}/status`, {
      status,
      comment,
    })
    return data
  }

  async addContractReviewComment(
    id: string,
    payload: { body: string; comment_type?: string; is_internal?: boolean }
  ): Promise<ContractReviewComment> {
    const { data } = await this.client.post<ContractReviewComment>(
      `/contract-reviews/${id}/comments`,
      payload
    )
    return data
  }

  async deleteContractReview(id: string): Promise<void> {
    await this.client.delete(`/contract-reviews/${id}`)
  }
}

export const apiClient = new ApiClient()
